import { fabric } from 'fabric';
import type ControlBase from 'fabric/ControlBase';
import { type Point } from 'fabric/fabric-impl';
import { range } from 'lodash';
import sanitizeHtml from 'sanitize-html';
import tableIcon from '../assets/icons/table.svg';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import TableLinearResizeControl from '../controls/TableLinearResizeControl';
import TableResizeControl from '../controls/TableResizeControl';
import TableVectorAddControl from '../controls/TableVectorAddControl';
import TableVectorDragControl from '../controls/TableVectorDragControl';
import TableVectorResizeControl from '../controls/TableVectorResizeControl';
import { type ToolList } from '../fabric/ObjectBehaviors';
import tools from '../tools';
import {
  type AttachmentPoint,
  type ConnectControlWithNormalDefinition,
  type Bounds,
  type Connectable,
  type HasEmbeddedText,
  type Rect,
} from '../types';
import trackEvent from '../utils/analytics';
import { isRightClick } from '../utils/click';
import { pointInRect, pointNearLine } from '../utils/geometry';
import { binSearch } from '../utils/searchUtils';
import { _isMac } from '../utils/Shortcut';
import { SPACER_2 } from '../utils/style';
import { svgToIcon } from '../utils/svg';
import AnalyticsBehaviors from './AnalyticsBehaviors';
import CollaborationBehaviors from './CollaborationBehaviors';
import CommentableBehaviors from './CommentableBehaviors';
import ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import EmbeddedTextBehaviorsV2, {
  TEXT_MARKS,
  type TextProps,
} from './EmbeddedTextBehaviorsV2';
import FrameBehaviors, { HOVER_SHADOW_COLOR } from './FrameBehaviors';
import FrameMemberBehaviors from './FrameMemberBehaviors';
import { isConnector, isSimpleGroup, isSimpleSelection } from './guards';
import HighlightBehaviors from './HighlightBehaviors';
import LockBehaviors from './LockBehaviors';
import ReadOnlyBehaviors from './ReadOnlyBehaviors';
import ShapePersistentControlBehaviors from './ShapePersistentControlBehaviors';
import SmartGuideBehaviors from './SmartGuideBehaviors';
import StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import TextField, { TEXTFIELD_PROPS } from './TextField';
import TitleBehaviors from './TitleBehaviors';
import VotingBehaviors from './VotingBehaviors';
import { standardInboundConnectControlDefinition } from '../utils/connectors';

const HEADER_FILL_COLOR = '#E1E1E1'; // aha-gray-400
// Vertical space above frame for the text.
export const MIN_COLUMN_WIDTH = 50;
export const MIN_ROW_HEIGHT = 50;
const SELECTION_DISTANCE = 75;
const NEW_COLUMN_WIDTH = 250;
const NEW_ROW_HEIGHT = 150;

type Cell = [number, number];
type MergedCell = [Cell, Cell];
type MergedCellKey = `merged_cell_${number}_${number}_${number}_${number}`;

interface TableMergedCellKeys {
  [key: MergedCellKey]: boolean | undefined | null;
}

interface CellDimensions extends Rect {
  row: number;
  column: number;
}

type CellPropertyKey = string | ['textFields', string, string];
type CellProperties = Array<[CellPropertyKey, any]>;

interface Table
  extends FrameBehaviors,
    TitleBehaviors,
    ConnectorAttachmentBehaviors,
    SmartGuideBehaviors,
    EmbeddedTextBehaviorsV2,
    HasEmbeddedText,
    TableMergedCellKeys,
    LockBehaviors,
    CommentableBehaviors,
    HighlightBehaviors,
    FrameMemberBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    VotingBehaviors,
    ShapePersistentControlBehaviors {}

@FrameBehaviors
@TitleBehaviors
@ConnectorAttachmentBehaviors
@SmartGuideBehaviors
@EmbeddedTextBehaviorsV2
@LockBehaviors
@CommentableBehaviors
@HighlightBehaviors
@FrameMemberBehaviors
@AnalyticsBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@VotingBehaviors
@ShapePersistentControlBehaviors
class Table
  extends StyleCopyPasteBehaviors(fabric.Object)
  implements Connectable
{
  type = 'table';
  noScaleCache = false;
  selectionFullyContained = true;
  objectCaching = false;
  minScaleLimit = 0.12;
  strokeUniform = true;

  textProps: TextProps = {
    allowedStyles: 'simple',
    location: 'above',
    textAlign: 'left',
    verticalAlign: 'center',
    fontSize: 'fixed',
    overflow: 'visible',
    wrapText: 'growText',
    margins: 'noScale',
  };

  cellTextProps: TextProps = {
    allowedStyles: 'any',
    location: 'inside',
    textAlign: 'user',
    verticalAlign: 'user',
    fontSize: 'user',
    overflow: 'clip',
    wrapText: 'wrapGrow',
    margins: 'scale',
  };

  numColumns: number;
  numRows: number;
  selected = false;
  // Previously selected cell, used in cell multiselect
  previousCell: Cell | null = null;
  // Currently selected (and highlighted) cell
  currentCell: Cell | null = null;
  // Indicates if user is attempting to multiselect cells based on shift key
  shouldMultiSelectCell = false;
  // Merged cells are signified by the tuples representing the start and end cells
  mergedCells: MergedCell[] = [];
  // Whether another object is currently being dragged over this table.
  hovered = false;
  // Indicates to fabric that an object allows groupSelectors to be created within
  // this object
  allowsDragSelection = false;
  // Indicates that there was a groupSelector just prior to a given mouseup event,
  // in which case we should rely on the groupSelector to handle table selection
  _shouldSelectTable = false;
  // Holds the center points of objects dragged over the table. For groups and
  // individual objects, this will be the center point of the object. For
  // selections, this will be the set of center points of each object, allowing
  // for multiple cells to be highlighted
  hoveredPoints: Array<{ x: number; y: number }> | null;
  // Dimensions of vector to be drawn while dragging vector
  draggedVectorLeft;
  draggedVectorTop;
  // Indicates row/column edge to highlight to indicate destination of drag
  draggedVectorEndIndex;
  // Temp values to allow styling on cells
  tableFillColor: string | null | undefined = null;
  tableOpacity: number | undefined;
  hasHeaderRow = false;
  hasHeaderColumn = false;
  alternatingRowColor: string = 'transparent';
  alternatingRowOpacity = 1;

  // Avoid showing guides when dragging table that containns objects
  disableSmartGuides = true;

  // Properties we will serialized for each cell
  cellProperties = ['fill', 'opacity'];

  cellTextProperties = [
    ...TEXTFIELD_PROPS,
    ...TEXT_MARKS,
    'isTextBold',
    'isTextItalic',
    'isTextCode',
    'isTextStrikethrough',
    'isTextUnderline',
    'textListType',
  ];

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = [
    'z',
    'textContent',
    'left',
    'top',
    'width',
    'height',
    'scaleX',
    'scaleY',
    'fill',
    'stroke',
    'strokeWidth',
    'opacity',
    'borderOpacity',
    'container',
    'layoutPosition',
    'layoutManual',

    // Table specific keys
    'numColumns',
    'numRows',
    /^col_[0-9]+_width$/,
    /^row_[0-9]+_height$/,
    // Represent merged cells as the row, col of the start and end cells
    /^merged_cell_[0-9]+_[0-9]+_[0-9]+_[0-9]+$/,
    ...this.cellProperties.map(prop => RegExp(`^cell_[0-9]+_[0-9]+_${prop}$`)),
    'textFields',
    'hasHeaderRow',
    'hasHeaderColumn',
    'alternatingRowColor',
    'alternatingRowOpacity',
  ];

  controls: Record<string, ControlBase> = {
    tl: new TableResizeControl({ x: -0.5, y: -0.5 }),
    tr: new TableResizeControl({ x: 0.5, y: -0.5 }),
    bl: new TableResizeControl({ x: -0.5, y: 0.5 }),
    br: new TableResizeControl({ x: 0.5, y: 0.5 }),
    // Figure out UX around dragging vs scaling on borders
    mt: new TableLinearResizeControl({ edge: 'top' }),
    ml: new TableLinearResizeControl({ edge: 'left' }),
    // We don't have bottom/right controls here as we adjust the width
    // of the final column/row instead
  };

  static possibleAttachmentPoints: AttachmentPoint[] = Object.keys(
    Table.connectControlDefinition()
  ) as AttachmentPoint[];

  // Array of objects in frame
  _containedObjects: fabric.Object[];

  constructor(options) {
    super({});

    // _textFields need to be initialized before calling super.setOptions. Some
    // of the keys that get set in setOptions trigger methods that try to access
    // textFields.
    this.addText(options.textContent);

    super.setOptions({
      ...options,
      numColumns: options.numColumns || 3,
      numRows: options.numRows || 3,
    });

    // List of objects contained in this table. Only valid during a move.
    this._containedObjects = [];

    this.initTableMouseListeners();

    this.on('keydown', eventData => {
      void this._handleKeyDown(eventData.e);
    });

    this.on('selected', () => {
      if (this.hasSelectedCell() || this.hasMultipleSelectedCells()) {
        this.allowsDragSelection = true;
      } else {
        this.allowsDragSelection = false;
      }
    });

    this.on('deselected', () => {
      this._clearSelected();
      this.clearSelectedCells();
      this.controller.actionEnableToolKeys();
    });

    this.on('moving', () => {
      this._clearSelected();
      this.clearSelectedCells();
    });

    // Individual cells should not be selected when table is in selection
    this.on('group:added', () => this.clearSelectedCells());

    this.needsZoomEvents();
    this.initializeConnectorAttachments();
    this.initializeSmartGuides();
    this.initializeFrameMember();
    this.enableFrameBehaviors();
    this.initializeLockBehaviors(options.locked);

    // Set vector dimensions based on height width to handle drag to create case
    let columnWidth = NEW_COLUMN_WIDTH;
    let rowHeight = NEW_ROW_HEIGHT;
    if (!options.numColumns && !options.numRows) {
      columnWidth = options.width / 3;
      rowHeight = options.height / 3;
    }
    this._initializeVectors(rowHeight, columnWidth);
    this._initializeTextFields();
    this._validateMergedCells();

    this.textAlign ||= 'left';
    this.verticalAlign ||= 'flex-start';

    this.normalizeDimensions();
  }

  attachmentPoints(): AttachmentPoint[] {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.possibleAttachmentPoints;
  }

  connectControlDefinition() {
    // @ts-expect-error TS doesn't like accessing constructor like this
    return this.constructor.connectControlDefinition();
  }

  // Definition of the connector controls for inbound connections.
  // Includes position & normal information so that we can steer connectors away
  // from this table.
  static connectControlDefinition(): ConnectControlWithNormalDefinition {
    return standardInboundConnectControlDefinition();
  }

  allowedStyleCopyPasteFields(): string[] {
    const copyPasteProps = [
      'fill',
      'stroke',
      'strokeWidth',
      'opacity',
      'alternatingRowColor',
      'alternatingRowOpacity',
    ];
    if (this.hasSelectedCell()) {
      // Include properties for table cells
      return [
        ...this.cellProperties,
        ...copyPasteProps,
        'textColor',
        'fontSize',
        'fontFamily',
        'textOpacity',
      ];
    }
    return copyPasteProps;
  }

  // Gets the style value for a cell property in the currently selected cell
  getCellStyle(field: string): any {
    if (!this.hasSelectedCell()) return undefined;

    // First check if it's a cell property like fill or opacity
    if (this.cellProperties.includes(field)) {
      const [row, column] = this.currentCell;
      const cellKey = this._getCellKey(row, column, field);
      const value = this[cellKey];
      if (value !== undefined) {
        return value;
      }
      // Fall back to table's value if the cell doesn't have a value
      if (this[field] !== undefined) {
        return this[field];
      }
    }

    // Then check if it's a text field property
    const selectedFields = this.selectedTextFields;
    if (selectedFields.length > 0 && field in selectedFields[0]) {
      return selectedFields[0][field];
    }

    return undefined;
  }

  get votingResultIcon(): JSX.Element {
    return svgToIcon(tableIcon);
  }

  get votingResultSummary(): string {
    return `Table (${this.numColumns} x ${this.numRows})`;
  }

  deleted() {
    // Delete all the objects in the table
    for (let i = 0; i < this.numRows; i++) {
      this.deleteObjectsInVector(i, true, {
        excludeObjectsInMergedCell: false,
      });
    }
    this.connectorAttachments.forEach(attachment =>
      attachment.connector.detachObject(this)
    );
    trackEvent('Feature-7176742954015814903', 'User deletes whiteboard table');

    this.controller.actionEnableToolKeys();
    super.deleted(this.controller);
  }

  tools(): ToolList {
    // show tools for selected cell
    if (this.hasSelectedCell()) {
      // Always show text formatting tools for selected cells, even when empty
      const textFormatTools: ToolList = [
        [
          tools.ToolTextColor,
          tools.ToolTextFormat,
          tools.ToolTextList,
          tools.ToolTextAlign,
        ],
      ];
      if (this.textProps.fontSize !== 'scaleToFit') {
        textFormatTools.push([tools.ToolFontSize]);
      }

      const cellTools: ToolList = [[tools.ToolFillColor], ...textFormatTools];

      // Add tools for merging cells
      if (this.mergeable || this.unmergeable) {
        cellTools.push(
          this.mergeable
            ? tools.ToolTableMergeCells
            : tools.ToolTableUnmergeCells
        );
      }

      return cellTools;
    }
    return [
      [tools.ToolAddRow, tools.ToolAddColumn],
      [tools.ToolTableHeaderOptions],
      [
        tools.ToolTableAlternatingRowColor,
        tools.ToolFillColor,
        tools.ToolBorderColorWithOpacity,
        tools.ToolStrokeWidth,
      ],
      this.textTools(),
    ];
  }

  // Init selectors
  initGroupSelectorListeners() {
    if (this.canvas) {
      const handleUpdate = () => this.groupSelectorUpdated();
      const handleClear = () => this.groupSelectorCleared();
      this.canvas.on('groupSelector:updated', handleUpdate);
      this.canvas.on('groupSelector:cleared', handleClear);

      this.on('removed', () => {
        this.canvas.off('groupSelector:updated', handleUpdate);
        this.canvas.off('groupSelector:cleared', handleClear);
      });
    }
  }

  groupSelectorUpdated() {
    // Highlight cells only if the groupSelector is partially intersecting this
    // table and no other objects
    if (
      this.canvas.groupSelectorIntersectsObject(this) &&
      !this.canvas.groupSelectorIntersectsOther(this) &&
      !this.canvas.groupSelectorContainsObject(this)
    ) {
      this.updateSelectedCellsForGroupSelector();
    } else {
      this.clearSelectedCells();
    }
  }

  groupSelectorCleared() {
    this.handleTableSelection();
    if (
      (this.hasSelectedCell() || this.hasMultipleSelectedCells()) &&
      this.canvas.getActiveObject() !== this
    ) {
      this.clearSelectedCells();
    }
  }

  isSelectingCells() {
    return this.previousCell !== null && this.currentCell !== null;
  }

  private updateSelectedCellsForGroupSelector() {
    if (this.locked) {
      return;
    }

    if (this.canvas.hasGroupSelector && this.canvas.groupSelectorTlBr) {
      const { tl, br } = this.canvas.groupSelectorTlBr;
      const cell1 = this.getCellFromGroupSelectorPoint(tl);
      const cell2 = this.getCellFromGroupSelectorPoint(br);
      if (cell1 && cell2) {
        this.previousCell = cell1;
        this.currentCell = cell2;
        this.shouldMultiSelectCell = true;
        this.canvas.requestRenderAll();
      }
    }
  }

  getCellFromGroupSelectorPoint(pointer: fabric.IPoint): Cell | undefined {
    const startX = this.left;
    const endX = this.left + this.getScaledWidth();
    const startY = this.top;
    const endY = this.top + this.getScaledHeight();

    const cell = this._getCellDimensionsFromPointer(pointer, false);
    if (cell) {
      return [cell.row, cell.column];
    } else {
      const maybeCell = {} as { row?: number; column?: number };
      if (pointer.x > startX && pointer.x < endX) {
        const tempCell = this._getCellDimensionsFromPointer(
          {
            x: pointer.x,
            y: this.top + this.strokeWidth,
          },
          false
        );
        if (tempCell) {
          maybeCell.column = tempCell.column;
        }
      } else if (pointer.x < startX) {
        maybeCell.column = 0;
      } else if (pointer.x > endX) {
        maybeCell.column = this.numColumns - 1;
      }
      if (pointer.y > startY && pointer.y < endY) {
        const tempCell = this._getCellDimensionsFromPointer(
          {
            x: this.left + this.strokeWidth,
            y: pointer.y,
          },
          false
        );
        if (tempCell) {
          maybeCell.row = tempCell.row;
        }
      }
      if (pointer.y < this.top) {
        maybeCell.row = 0;
      } else if (pointer.y > this.top + this.getScaledHeight()) {
        maybeCell.row = this.numRows - 1;
      }
      if (
        typeof maybeCell.row === 'number' &&
        typeof maybeCell.column === 'number'
      ) {
        return [maybeCell.row, maybeCell.column];
      }
    }
  }

  handleTableSelection() {
    if (
      this.hasSelectedCell() ||
      (this.hasMultipleSelectedCells() && !this.canvas.getActiveObject())
    ) {
      this.canvas.setActiveObject(this);
      this.canvas.requestRenderAll();
    }

    if (
      (this.canvas.getActiveObject() === this && this.hasSelectedCell()) ||
      this.hasMultipleSelectedCells()
    ) {
      this.allowsDragSelection = true;
    }
  }

  _initializeVectors(rowHeight, columnWidth) {
    this.setCoords();
    const { numColumns, numRows } = this;

    // Initialize the controls for each row and column
    for (let i = 0; i < numRows; i++) {
      const key = this.getVectorDimensionKey(i, true);
      if (!this[key]) {
        this.set(key, rowHeight);
      }
      const controlKey = this._getVectorResizeControlKey(i, true);
      if (!this[controlKey]) {
        this.controls[controlKey] = new TableVectorResizeControl(i, true);
      }
    }
    for (let i = 0; i < numColumns; i++) {
      const key = this.getVectorDimensionKey(i, false);
      if (!this[key]) {
        this.set(key, columnWidth);
      }
      const controlKey = this._getVectorResizeControlKey(i, false);
      if (!this[controlKey]) {
        this.controls[controlKey] = new TableVectorResizeControl(i, false);
      }
    }

    this.controls.addRow = new TableVectorAddControl(true);
    this.controls.addColumn = new TableVectorAddControl(false);
  }

  _showVectorDragControls(pointer) {
    // Avoid changing drag control visibility while dragging
    if (this.__corner?.toString().toLowerCase().includes('drag')) return;

    const zoom = this.canvas.getZoom();
    const radius = SELECTION_DISTANCE / zoom;
    // Hide all vector drag controls
    for (const [key, value] of Object.entries(this.controls)) {
      if (key.includes('Drag')) {
        value.setVisibility(false);
      }
    }

    // Adjust the pointer by the selection distance to find the valid cell
    if (pointer.x < this.left) {
      pointer.x += radius;
    } else if (pointer.x > this.left + this.getScaledWidth()) {
      pointer.x -= radius;
    }
    if (pointer.y < this.top) {
      pointer.y += radius;
    } else if (pointer.y > this.top + this.getScaledHeight()) {
      pointer.y -= radius;
    }

    // Find row/col for current cell and show drag controls for those vectors
    const cell = this._getCellDimensionsFromPointer(pointer);
    if (cell) {
      const { row, column } = cell;
      const rowKey = `rDrag_${row}`;
      const colKey = `cDrag_${column}`;

      if (!this[rowKey]) {
        this.controls[rowKey] = new TableVectorDragControl({
          index: row,
          draggingVertical: true,
        });
      }
      if (!this[colKey]) {
        this.controls[colKey] = new TableVectorDragControl({
          index: column,
          draggingVertical: false,
        });
      }
      this.controls[rowKey].setVisibility(true);
      this.controls[colKey].setVisibility(true);
      this.controller.canvas.requestRenderAll();
    }
  }

  _initializeTextFields() {
    // Use TextField object to create text for each cell in the table that has
    // text content
    for (let i = 0; i < this.numRows; i++) {
      for (let j = 0; j < this.numColumns; j++) {
        const textField = this.createTextField(i, j);
        if (textField) {
          this.addTextField(textField);
        }
      }
    }

    this.updateTextFieldDimensions();
  }

  addVector(
    index: number,
    dimension: number,
    isRow = true,
    originalIndex?: number
  ) {
    this._updateMergedCellsForVectorCount(index, {
      isRow,
      adding: true,
      originalIndex,
    });

    // Increase number of this vector
    const vectorCountKey = isRow ? 'numRows' : 'numColumns';
    this.set(vectorCountKey, this[vectorCountKey] + 1);

    // Add width/height to table
    let tableDimensionAdd;
    if (dimension) {
      tableDimensionAdd = dimension;
    } else {
      tableDimensionAdd = isRow ? NEW_ROW_HEIGHT : NEW_COLUMN_WIDTH;
    }
    const tableDimKey = isRow ? 'height' : 'width';
    this.set(tableDimKey, this[tableDimKey] + tableDimensionAdd);

    // Shift affected vectors right/down
    for (let i = this[vectorCountKey] - 1; i > index; i--) {
      const currentDimKey = this.getVectorDimensionKey(i, isRow);
      const nextDimKey = this.getVectorDimensionKey(i - 1, isRow);
      this.set(currentDimKey, this[nextDimKey]);
    }

    // Set dimension of new vector
    const vectorDimKey = this.getVectorDimensionKey(index, isRow);
    this.set(vectorDimKey, tableDimensionAdd);

    // Shift vector contents by added vector dimension
    this.shiftObjectsAfterVectorIndex(index - 1, tableDimensionAdd, isRow);
    // Shift cell properties for each vector
    for (let i = this[vectorCountKey] - 1; i > index; i--) {
      this._shiftVectorCells(i, isRow);
    }

    this._updateTextFields();

    this.canvas.requestRenderAll();

    trackEvent(
      'Feature-7176742954015814903',
      'User adds whiteboard table cells'
    );
  }

  // Helper to add a vector to the end of the table
  addVectorToEnd(isRow = true) {
    const vectorCount = isRow ? this.numRows : this.numColumns;
    const dimension = this[this.getVectorDimensionKey(vectorCount - 1, isRow)];
    this.addVector(vectorCount, dimension, isRow);
  }

  // Add vector via context menu. isLeft represents adding a vector to the left
  // or above the current vector, depending on isRow
  addVectorOnClick(isLeft = true, isRow = true): void {
    const canvasPoint = this.canvas.pointToCanvasCoords(
      this.controller.contextMenuClickPoint
    );
    const cell = this._getCellDimensionsFromPointer(canvasPoint, false);
    if (cell === null) {
      // If the context menu click did not happen in a cell, do not add a vector
      console.error('Invalid cell, cannot add vector');
      return;
    }

    // Get this vector's dimension
    const index = isRow ? cell.row : cell.column;
    const insertionIndex = isLeft ? index : index + 1;
    const dimension = this[this.getVectorDimensionKey(index, isRow)];

    // Adjust index if adding to left of/above table
    this.addVector(
      insertionIndex,
      dimension,
      isRow,
      isLeft ? index - 1 : index
    );

    // Remove cell properties on added vector
    this._removeVectorCellsProperties(insertionIndex, isRow);

    if (this.currentCell) {
      const [row, col] = this.currentCell;
      if (isRow) {
        this.currentCell = isLeft ? [row + 1, col] : [row, col];
      } else {
        this.currentCell = isLeft ? [row, col + 1] : [row, col];
      }
    }
  }

  deleteVector(index: number, isRow = true) {
    if (this.vectorIntersectsAnyPerpendicularMergedCell(index, isRow)) {
      this.controller.actionShowMessage(
        `Unmerge cells before deleting ${isRow ? 'row' : 'column'}`
      );
      return;
    }

    this._updateMergedCellsForVectorCount(index, { isRow, adding: false });

    this.clearSelectedCells();

    const key = this.getVectorDimensionKey(index, isRow);
    const dimension = this[key];
    const vectorCountKey = isRow ? 'numRows' : 'numColumns';
    const tableDimKey = isRow ? 'height' : 'width';

    // If there is only one vector left, just delete the table
    if (this[vectorCountKey] === 1) {
      const canvas = this.canvas;
      this.deleted();
      canvas.remove(this);
      return;
    }

    // Remove vector contents, except those in merged cells
    this.deleteObjectsInVector(index, isRow, {
      excludeObjectsInMergedCell: true,
    });

    // Shift vector contents by deleted vector dimension
    this.shiftObjectsAfterVectorIndex(index, -dimension, isRow, {
      excludeObjectsInMergedCell: false,
    });

    for (let i = index; i < this[vectorCountKey] - 1; i++) {
      const currentDimKey = this.getVectorDimensionKey(i, isRow);
      const nextDimKey = this.getVectorDimensionKey(i + 1, isRow);
      // Shift vector dimensions
      this.set(currentDimKey, this[nextDimKey]);

      this._shiftVectorCells(i, isRow, false);
    }

    // Set last instance of vector keys to null
    const dimKey = this.getVectorDimensionKey(this[vectorCountKey] - 1, isRow);
    this.set(dimKey, null);

    // Remove cell properties of last vector instance
    this._removeVectorCellsProperties(this[vectorCountKey] - 1, isRow);

    // Reduce number of this vector
    this.set(vectorCountKey, this[vectorCountKey] - 1);
    // Reduce table width/height
    this.set(tableDimKey, this[tableDimKey] - dimension);
    this.canvas.requestRenderAll();

    trackEvent(
      'Feature-7198326346420158012',
      'User removes whiteboard table cells'
    );
  }

  // Deletes all rows/columns for the currently selected cell or group of cells
  deleteVectorsOnClick(isRow = true) {
    if (this.controller.contextMenuClickPoint) {
      const canvasPoint = this.canvas.restorePointerVpt(
        this.controller.contextMenuClickPoint
      );
      const cellDimensions = this._getCellDimensionsFromPointer(canvasPoint);

      if (cellDimensions === null) {
        console.error('Invalid cell, cannot delete vectors');
        return;
      }
      const cell: Cell = [cellDimensions.row, cellDimensions.column];
      const [cell1, cell2] = this.hasMultipleSelectedCells()
        ? this._getCellRange(this.currentCell, this.previousCell)
        : [cell, cell];

      // If somehow the clicked-on cell is not in the currently selected cells,
      // do nothing. At this point, we would delete something other than what is
      // show to be selected, which would be confusing
      if (!this.cellInRange(cell, cell1, cell2)) {
        console.error('Selected cell not in currently selected cells');
        return;
      }

      const start = isRow ? cell1[0] : cell1[1];
      const end = (isRow ? cell2[0] : cell2[1]) + 1;
      let count = end - start;

      // Don't perform delete operation if this group of cells intersects any
      // merged cells
      if (
        range(start, end).some(i =>
          this.vectorIntersectsAnyPerpendicularMergedCell(i, isRow)
        )
      ) {
        this.controller.actionShowMessage(
          `Unmerge cells before deleting ${isRow ? 'rows' : 'columns'}`
        );
        return;
      }

      while (count > 0) {
        this.deleteVector(start, isRow);
        count--;
      }

      this.clearSelectedCells();
      this.controller.actionRerenderToolbar();
    }
  }

  // When adding or deleting a row/column, ensure that the table dimensions match
  // the sum of the vector dimensions. Occasionally these can get out of sync,
  // leading to "phantom" rows/columns that cannot be deleted
  normalizeDimensions() {
    const { width, height, numRows, numColumns } = this;
    let totalHeight = 0;
    let totalWidth = 0;
    for (let i = 0; i < numRows; i++) {
      const rowHeight = this[this.getVectorDimensionKey(i, true)];
      totalHeight += rowHeight;
    }
    for (let i = 0; i < numColumns; i++) {
      const columnWidth = this[this.getVectorDimensionKey(i, false)];
      totalWidth += columnWidth;
    }

    // We have an extra row. The simplest solution is to increate the height of
    // the table to include this row
    if (totalHeight > height) {
      this.set('height', totalHeight);
    } else {
      // Most likely missing a row. The simplest solution is to make the last
      // row taller
      const diff = height - totalHeight;
      const key = this.getVectorDimensionKey(numRows - 1, true);
      const lastRowHeight = this[key];
      this.set(key, diff + lastRowHeight);
    }

    // Similar to above, we have an extra column. The simplest solution is to
    // increase the width of the table to include this column
    if (totalWidth > width) {
      this.set('width', totalWidth);
    } else {
      // Most likely missing a column. The simplest solution is to make the last
      // column wider
      const diff = width - totalWidth;
      const key = this.getVectorDimensionKey(numColumns - 1, false);
      const lastColumnWidth = this[key];
      this.set(key, diff + lastColumnWidth);
    }
  }

  // Returns the furthest left, top, right, and bottom values for the objects
  // within a given vector
  vectorObjectsBoundingBox(
    index: number,
    isRow = true,
    objects: fabric.Object[] | null = null
  ) {
    if (!objects) {
      objects ||= this.objectsInVector(index, isRow);
    }

    if (objects.length === 0) return null;

    let boxLeft = Number.MAX_SAFE_INTEGER;
    let boxTop = Number.MAX_SAFE_INTEGER;
    let boxRight = Number.MIN_SAFE_INTEGER;
    let boxBottom = Number.MIN_SAFE_INTEGER;

    for (let i = 0; i < objects.length; i++) {
      const object = objects[i];
      const { left, top } = object;
      const width = object.getScaledWidth();
      const height = object.getScaledHeight();

      boxLeft = Math.min(boxLeft, left);
      boxTop = Math.min(boxTop, top);
      boxRight = Math.max(boxRight, left + width);
      boxBottom = Math.max(boxBottom, top + height);
    }

    return {
      left: boxLeft,
      top: boxTop,
      right: boxRight,
      bottom: boxBottom,
    };
  }

  objectsBoundingBox(objects: fabric.Object[] = []): Bounds | null {
    if (objects.length === 0) {
      return null;
    }

    let boxLeft = Number.MAX_SAFE_INTEGER;
    let boxTop = Number.MAX_SAFE_INTEGER;
    let boxRight = Number.MIN_SAFE_INTEGER;
    let boxBottom = Number.MIN_SAFE_INTEGER;

    for (let i = 0; i < objects.length; i++) {
      const object = objects[i];

      // Skip connectors with attachments when determing bounding box. We don't
      // want these connectors to affect resizing, and instead they should just
      // grow/shrink with the direction their attachments move
      if (isConnector(object) && object.attached()) {
        continue;
      }

      const { width, height, left, top } = object.getBoundingRect(true);

      boxLeft = Math.min(boxLeft, left);
      boxTop = Math.min(boxTop, top);
      boxRight = Math.max(boxRight, left + width);
      boxBottom = Math.max(boxBottom, top + height);
    }

    return {
      left: boxLeft,
      top: boxTop,
      right: boxRight,
      bottom: boxBottom,
    };
  }

  deleteObjectsInVector(
    index: number,
    isRow = true,
    { excludeObjectsInMergedCell = false } = {}
  ): void {
    let objects = this.objectsInVector(index, isRow, true);
    if (excludeObjectsInMergedCell) {
      objects = objects.filter(object => {
        return !this._objectIsInIntersectingMergedCell(object, index, isRow);
      });
    }
    for (const object of objects) {
      if (object.deleted) {
        object.deleted(this.controller);
        if (this.canvas) {
          this.canvas?.remove(object);
        }
        this._containedObjects = this._containedObjects.filter(
          obj => obj.id !== object.id
        );
      }
    }
  }

  shiftObjectsInVector(
    index: number,
    amount: number,
    isRow = true,
    { excludeObjectsInMergedCell = false } = {}
  ) {
    const vectorCount = isRow ? this.numRows : this.numColumns;
    if (index < 0 || index >= vectorCount) {
      return;
    }
    let objects = this.objectsInVector(index, isRow);
    if (excludeObjectsInMergedCell) {
      objects = objects.filter(
        obj => !this._objectIsInIntersectingMergedCell(obj, index, isRow)
      );
    }
    this.shiftObjects(objects, amount, isRow);
  }

  shiftObjects(objects: fabric.Object[], amount: number, isRow = true) {
    objects.forEach(obj => {
      const topLeft = isRow ? 'top' : 'left';
      const left = isRow ? obj.left : obj[topLeft] + amount;
      const top = isRow ? obj[topLeft] + amount : obj.top;
      obj.moveToCoords({ left, top });
    });
  }

  shiftObjectsAfterVectorIndex(
    index: number,
    amount: number,
    isRow = true,
    { excludeObjectsInMergedCell = true } = {}
  ): void {
    const vectorCount = isRow ? this.numRows : this.numColumns;
    let objects: fabric.Object[] = [];
    for (let i = index + 1; i < vectorCount; i++) {
      objects.push(...this.objectsInVector(i, isRow));
    }
    if (excludeObjectsInMergedCell) {
      objects = objects.filter(
        object => !this._objectIsInIntersectingMergedCell(object, index, isRow)
      );
    }

    this.shiftObjects(objects, amount, isRow);
  }

  _shiftVectorCells(index: number, isRow = true, forwards = true) {
    const next = forwards ? index - 1 : index + 1;
    const cellProperties = this._getVectorCellsProperties(next, isRow);
    this._applyVectorCellsProperties(index, cellProperties, isRow);
  }

  // Applies properties from vectorCellProperties to the destination vector at index
  _applyVectorCellsProperties(
    index: number,
    vectorCellsProperties: CellProperties,
    isRow = true
  ) {
    for (const [key, value] of vectorCellsProperties) {
      // Special handling for cell text properties
      if (Array.isArray(key) && key[0] === 'textFields') {
        const [row, column] = this._parseCellKey(key[1]);
        const textFieldKey = this._getCellKey(
          isRow ? index : row,
          isRow ? column : index
        );

        // Handle bug where textFieldKey does not match an existing text field.
        // This should prevent any crashing errors for the end user and provide
        // some basic logging to help gain insight into this issue
        if (!this._textFields[textFieldKey]) {
          console.error(
            'no text field found for',
            textFieldKey,
            Object.keys(this._textFields)
          );
        } else if (this._textFields[textFieldKey][key[2]] !== value) {
          this._textFields[textFieldKey]?.set(key[2], value);
        }
      } else if (typeof key === 'string') {
        const [row, column, prop] = this._parseCellKey(key);
        const destCellKey = this._getCellKey(
          isRow ? index : row,
          isRow ? column : index,
          prop
        );

        this.set(destCellKey, value);
      }
    }
  }

  _getVectorCellsProperties(index: number, isRow = true): CellProperties {
    const cells = this._getVectorCellsCoords(index, isRow);
    const cellProperties: CellProperties = [];
    for (const [row, col] of cells) {
      this.cellProperties.forEach(prop => {
        const key = this._getCellKey(row, col, prop);
        cellProperties.push([key, this[key]]);
      });

      const textFieldKey = this._getCellKey(row, col);
      const textField = this._textFields && this._textFields[textFieldKey];
      this.cellTextProperties.forEach(prop => {
        const value = textField != null ? textField[prop] : null;
        cellProperties.push([['textFields', textFieldKey, prop], value]);
      });
    }
    return cellProperties;
  }

  _removeVectorCellsProperties(index: number, isRow = true) {
    const cells = this._getVectorCellsCoords(index, isRow);
    for (const [row, col] of cells) {
      this.cellProperties.forEach(prop => {
        const key = this._getCellKey(row, col, prop);
        if (this[key] != null) {
          this.set(key, null);
        }
      });

      // Remove text field doc data for this vector, but leave TextField objects
      const textFieldKey = this._getCellKey(row, col);
      const textField = this._textFields[textFieldKey];
      if (textField) {
        this.cellTextProperties.forEach(prop => {
          if (textField[prop] != null) {
            textField.set(prop, null);
          }
        });
      }
    }
  }

  // Applies the cell properties to all cells in the range
  _applyCellPropertiesForRange(
    cell1: Cell,
    cell2: Cell,
    key: string,
    value: any
  ): void {
    for (let i = cell1[0]; i <= cell2[0]; i++) {
      for (let j = cell1[1]; j <= cell2[1]; j++) {
        const cellKey = this._getCellKey(i, j, key);
        if (value == null || this[key] != null) {
          this.set(cellKey, value);
        }
      }
    }
  }

  _applyMergedCellProperties(cell1: Cell, cell2: Cell): void {
    const properties = this.cellProperties.map(prop => {
      const key = this._getCellKey(cell1[0], cell1[1], prop);
      return [prop, this[key]]; // clear the property if none for this cell
    });

    properties.forEach(kv => {
      if (kv !== null) {
        this._applyCellPropertiesForRange(cell1, cell2, kv[0], kv[1]);
      }
    });

    if (this.hasTextFields) {
      const [r, c] = cell1;
      const startCellKey = this._getCellKey(r, c);
      const textContent = this._textFields[startCellKey]?.textContent;
      for (let i = r; i <= cell2[0]; i++) {
        for (let j = c; j <= cell2[1]; j++) {
          const textFieldKey = this._getCellKey(i, j);
          this._textFields[textFieldKey].set('textContent', null);
        }
      }

      // Handles flickering issue with measure text while cells are merging
      this._textFields[startCellKey]?.set('textContent', textContent);
    }
  }

  objectsInVectors(isRow = true): fabric.Object[][] {
    const vectorCount = isRow ? this.numRows : this.numColumns;
    const objects: fabric.Object[][] = [];
    for (let i = 0; i < vectorCount; i++) {
      objects.push(this.objectsInVector(i, isRow));
    }
    return objects;
  }

  objectsInVector(
    index: number,
    isRow = true,
    forceIncludeConnectors = false
  ): fabric.Object[] {
    const rect = this.getVectorRect({
      index,
      isRow,
      scaled: true,
    });
    if (rect) {
      const vectorObjects = this._objectsInDimensions(
        rect,
        forceIncludeConnectors
      );
      return vectorObjects;
    }
    return [];
  }

  _includeConnector(
    object,
    dimensions,
    forceIncludeConnectors = false
  ): boolean {
    const attachments = object.attachments;

    if (
      attachments[0] == null &&
      attachments[1] == null &&
      this._containsObject(dimensions, object)
    ) {
      return true;
    } else if (forceIncludeConnectors) {
      if (
        attachments[0] != null &&
        this._containsObject(dimensions, attachments[0].object)
      ) {
        return true;
      } else if (
        attachments[1] != null &&
        this._containsObject(dimensions, attachments[1].object)
      ) {
        return true;
      }
    }

    return false;
  }

  _objectsInDimensions(
    dimensions: Rect,
    forceIncludeConnectors = false
  ): fabric.Object[] {
    const objects = Array<fabric.Object>();
    for (let i = 0; i < this._containedObjects.length; i++) {
      const object = this._containedObjects[i];
      // Special treatment for connectors. If forceIncludeConnectors, include
      // the connector if either attachment is in this vector
      if (
        object.type === 'connector' &&
        this._includeConnector(object, dimensions, forceIncludeConnectors)
      ) {
        objects.push(object);
      } else if (this._containsObject(dimensions, object)) {
        objects.push(object);
      }
    }
    return objects;
  }

  mergeCells(): void {
    if (!this.currentCell || !this.previousCell) {
      return;
    }

    let [cell1, cell2] = this._getCellRange(
      this.currentCell,
      this.previousCell
    );

    if (this._inMergedCell(cell1)) {
      cell1 = this._getMergedCell(cell1)[0];
    }
    if (this._inMergedCell(cell2)) {
      cell2 = this._getMergedCell(cell2)[1];
    }

    if (cell1.length !== 2 || cell2.length !== 2) {
      return;
    }

    // Clear any existing merged cells
    this._removeMergedCellsInRange(cell1, cell2);
    // Apply properties from first cell in selection across the range
    this._applyMergedCellProperties(cell1, cell2);
    // Merge the cells
    this.set(this._getMergedCellKey([cell1, cell2]) as KeyPath, true);

    this.updateTextFieldDimensions();

    // Clear selected cells and set selected cell for highlight
    this.clearSelectedCells();
    this.currentCell = [...cell1];
    // Rerender toolbar to update merge tools
    this.controller.actionRerenderToolbar();
    this.canvas.requestRenderAll();
  }

  unmergeCells(): void {
    if (!this.currentCell) {
      return;
    }

    let textContent: string | null = null;
    let startCellKey;
    if (this.hasTextFields) {
      const [r, c] = this.currentCell;
      startCellKey = this._getCellKey(r, c);
      textContent = this._textFields[startCellKey]?.textContent;
      this._textFields[startCellKey].set('textContent', null);
    }

    const index = this._findMergedCellIndexForCell(this.currentCell);
    const mergedCell = this._removeMergedCellAtIndex(index);
    if (mergedCell) {
      this.set(this._getMergedCellKey(mergedCell) as KeyPath, null);
    }

    this.updateTextFieldDimensions();

    // Avoid flickering on text render
    this._textFields[startCellKey]?.set('textContent', textContent);

    // Select merged cell range after unmerging
    if (mergedCell != null) {
      const [start, end] = mergedCell;
      this.clearSelectedCells();
      this.currentCell = [...start];
      this.previousCell = [...end];
      this.shouldMultiSelectCell = true;
      // Rerender toolbar to update merge tools
      this.controller.actionRerenderToolbar();
      this.canvas.requestRenderAll();
    }
  }

  _addMergedCell(mergedCell: string): void {
    const cell = this._parseMergedCellKey(mergedCell);
    this.mergedCells.push(cell);
  }

  _removeMergedCell(mergedCell: string): void {
    const cell = this._parseMergedCellKey(mergedCell);
    const index = this._findMergedCellIndex(cell);
    this._removeMergedCellAtIndex(index);
  }

  // Adds/removes merged cells. Must only be called from _set
  _syncMergedCells(key: KeyPath, value: true | null): void {
    if (Array.isArray(key)) return;

    if (value) {
      this._addMergedCell(key);
    } else {
      this._removeMergedCell(key);
    }

    this.updateTextFieldDimensions();
  }

  _validateMergedCells() {
    const toRemove: MergedCell[] = [];
    this.mergedCells.forEach(cell => {
      const [start, end] = cell;
      const [startRow, startCol] = start;
      const [endRow, endCol] = end;
      if (
        startRow > endRow ||
        startCol > endCol ||
        startRow < 0 ||
        startCol < 0 ||
        endRow >= this.numRows ||
        endCol >= this.numColumns
      ) {
        toRemove.push(cell);
      }
    });

    this._updateMergedCells([], toRemove);
  }

  // Given a subcell, find the merged cell it belongs to, if any
  _findMergedCellIndexForCell(cell: Cell): number {
    const [row, col] = cell;
    for (let i = 0; i < this.mergedCells.length; i++) {
      const [start, end] = this.mergedCells[i];
      if (
        row >= start[0] &&
        row <= end[0] &&
        col >= start[1] &&
        col <= end[1]
      ) {
        return i;
      }
    }
    return -1;
  }

  _findMergedCellIndex(mergedCell: MergedCell): number {
    for (let i = 0; i < this.mergedCells.length; i++) {
      const [start, end] = this.mergedCells[i];
      const [startRow, startCol] = start;
      const [endRow, endCol] = end;
      const [mergedStart, mergedEnd] = mergedCell;
      const [mergedStartRow, mergedStartCol] = mergedStart;
      const [mergedEndRow, mergedEndCol] = mergedEnd;
      if (
        startRow === mergedStartRow &&
        startCol === mergedStartCol &&
        endRow === mergedEndRow &&
        endCol === mergedEndCol
      ) {
        return i;
      }
    }
    return -1;
  }

  _removeMergedCellAtIndex(index: number): MergedCell | null {
    if (index >= 0 && index < this.mergedCells.length) {
      return this.mergedCells.splice(index, 1)[0];
    }
    return null;
  }

  _removeMergedCellsInRange(cell1: Cell, cell2: Cell): void {
    const [[rowMin, colMin], [rowMax, colMax]] = this._getCellRange(
      cell1,
      cell2
    );
    const toRemove: MergedCell[] = [];
    this.mergedCells.forEach(cell => {
      const [start, end] = cell;
      const [startRow, startCol] = start;
      const [endRow, endCol] = end;
      if (
        rowMin <= endRow &&
        rowMax >= startRow &&
        colMin <= endCol &&
        colMax >= startCol
      ) {
        toRemove.push(cell);
      }
    });

    this._updateMergedCells([], toRemove);
  }

  _updateMergedCells(add: MergedCell[], remove: MergedCell[]) {
    if (remove.length > 0 || add.length > 0) {
      const removal = Object.fromEntries(
        remove.map(cell => [this._getMergedCellKey(cell), null])
      );
      const addition = Object.fromEntries(
        add.map(cell => [this._getMergedCellKey(cell), true])
      );
      this.set({ ...removal, ...addition } as Partial<this>);
    }
  }

  _getCellRange(cell1: Cell, cell2: Cell): [Cell, Cell] {
    const [r1, c1] = cell1;
    const [r2, c2] = cell2;
    let rowMin = Number.MAX_SAFE_INTEGER;
    let rowMax = Number.MIN_SAFE_INTEGER;
    let colMin = Number.MAX_SAFE_INTEGER;
    let colMax = Number.MIN_SAFE_INTEGER;

    rowMin = Math.min(r1, r2);
    rowMax = Math.max(r1, r2);
    colMin = Math.min(c1, c2);
    colMax = Math.max(c1, c2);

    return [
      [rowMin, colMin],
      [rowMax, colMax],
    ];
  }

  private cellInRange(cell: Cell, start: Cell, end: Cell): boolean {
    const range = this._getCellRange(start, end);
    const [row, col] = cell;
    const [rowMin, colMin] = range[0];
    const [rowMax, colMax] = range[1];
    return row >= rowMin && row <= rowMax && col >= colMin && col <= colMax;
  }

  // Checks if the given cell coordinates are part of a merged cell
  _inMergedCell(cell: Cell | null): boolean {
    if (!cell) {
      return false;
    }

    const [row, col] = cell;
    for (let i = 0; i < this.mergedCells.length; i++) {
      const [start, end] = this.mergedCells[i];
      if (
        row >= start[0] &&
        row <= end[0] &&
        col >= start[1] &&
        col <= end[1]
      ) {
        return true;
      }
    }

    return false;
  }

  _getMergedCell(cell: Cell): MergedCell {
    const index = this._findMergedCellIndexForCell(cell);
    return this.mergedCells[index];
  }

  // Indicates if a cell contains another cell. Primarily used for checking if
  // 'containedCell' is inside a MergedCell corresponding to 'cell'
  private cellContainsCell(cell: Cell, containedCell: Cell) {
    if (this._inMergedCell(cell)) {
      const mergedCell = this._getMergedCell(cell);
      return this.cellInRange(containedCell, mergedCell[0], mergedCell[1]);
    }
    return this._cellEquals(cell, containedCell);
  }

  // Checks if the given object is in a merged cell intersecting the vector index
  _objectIsInIntersectingMergedCell(
    object: fabric.Object,
    index: number,
    isRow = true
  ): boolean {
    const center = object.getCenterPoint();

    for (const cell of this.mergedCells) {
      const [start] = cell;
      if (this._vectorIntersectsMergedCell(index, isRow, cell)) {
        const cellDimensions = this._getCellDimensions(...start);
        if (cellDimensions != null) {
          const { left, top, width, height } = cellDimensions;
          if (
            center.x > left &&
            center.y > top &&
            center.x < left + width &&
            center.y < top + height
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  // Adjust merged cells when vectors added/removed.
  // -Intersected cells will grow/shrink. If there is only 1 subcell left on
  //  delete, remove the merged cell
  // -Merged cells behind the added/removed vector will shift accordingly
  // -Merged cells will keep cell properties if part of the merged cell is removed
  _updateMergedCellsForVectorCount(
    index: number,
    options: {
      isRow: boolean;
      adding: boolean;
      originalIndex?: number;
    }
  ): void {
    const { isRow, adding, originalIndex = -1 } = options;
    const rowOrCol = isRow ? 0 : 1;
    const toRemove: MergedCell[] = [];
    const toAdd: MergedCell[] = [];

    this.mergedCells.forEach(cell => {
      let currentCell: MergedCell | null = null;
      let newCell: MergedCell | null = null;

      // Adjust size of intersecting merged cells
      if (this._vectorIntersectsMergedCell(index, isRow, cell)) {
        currentCell = this._copyMergedCell(cell);
        newCell = this._copyMergedCell(cell);
        // Grow/shrink cells, depending on adding

        if (adding) {
          // If the original index does not intersect the merged cell,
          // shift the start row/col up
          if (!this._vectorIntersectsMergedCell(originalIndex, isRow, cell)) {
            newCell[0][rowOrCol]++;
          }
          newCell[1][rowOrCol]++;
        } else if (
          !this.vectorIntersectsPerpendicularMergedCell(index, isRow, cell)
        ) {
          toRemove.push(cell);
          currentCell = null;
          newCell = null;
        }
      } else if (index < cell[0][rowOrCol]) {
        // Shift cells forward/back, depending on adding
        currentCell = this._copyMergedCell(cell);
        newCell = this._copyMergedCell(cell);
        if (adding) {
          newCell[0][rowOrCol]++;
          newCell[1][rowOrCol]++;
        } else {
          newCell[0][rowOrCol]--;
          newCell[1][rowOrCol]--;
        }
      }

      if (currentCell) {
        toRemove.push(currentCell);
      }

      if (newCell && !this._isSingleCell(newCell)) {
        toAdd.push(newCell);
      }
    });

    this._updateMergedCells(toAdd, toRemove);
  }

  // Shifts merged cells based on vector drag results. Vector drag cannot intersect
  // with any merged cell
  _updateMergedCellsForVectorDrag(
    start: number,
    end: number,
    isRow: boolean
  ): void {
    const toRemove = Array<MergedCell>();
    const toAdd = Array<MergedCell>();
    const isForwardsDrag = end > start;

    this.mergedCells.forEach(cell => {
      const [startCell, endCell] = cell;
      const [startRow, startCol] = startCell;
      const [endRow, endCol] = endCell;

      // Check if the vector drag cross over the merged cell.
      let shouldUpdate = true;
      if (isForwardsDrag) {
        shouldUpdate = isRow
          ? start <= startRow && end >= endRow
          : start <= startCol && end >= endCol;
      } else {
        shouldUpdate = isRow
          ? start >= startRow && end <= endRow
          : start >= startCol && end <= endCol;
      }

      if (shouldUpdate) {
        const newCell = this._copyMergedCell(cell);

        if (isForwardsDrag) {
          if (isRow) {
            newCell[0][0]--;
            newCell[1][0]--;
          } else {
            newCell[0][1]--;
            newCell[1][1]--;
          }
        } else {
          if (isRow) {
            newCell[0][0]++;
            newCell[1][0]++;
          } else {
            newCell[0][1]++;
            newCell[1][1]++;
          }
        }

        if (!this._mergedCellEquals(cell, newCell)) {
          toRemove.push(cell);
          toAdd.push(newCell);
        }
      }
    });

    this._updateMergedCells(toAdd, toRemove);
  }

  // Hides vector resize controls within merged cells.
  _handleMergedCellControls(eventData: fabric.IEvent): void {
    const { pointer } = eventData;
    if (pointer != null) {
      const canvasPoint = this.canvas.pointToCanvasCoords(pointer);
      const cell = this._getCellDimensionsFromPointer(canvasPoint);

      if (cell != null) {
        const inMergedCell = this._inMergedCell([cell.row, cell.column]);

        if (inMergedCell) {
          const zoom = this.canvas?.getZoom() ?? 1;
          const { left, top, width, height } = cell;
          const margin = 50 / zoom;
          // Check if on the outer edge of the merged cell. This is especially
          // important for adjacent merged cells
          const isOnVerticalEdge =
            inMergedCell &&
            (Math.abs(canvasPoint.x - left) < margin ||
              Math.abs(canvasPoint.x - (left + width)) < margin);
          const isOnHorizontalEdge =
            inMergedCell &&
            (Math.abs(canvasPoint.y - top) < margin ||
              Math.abs(canvasPoint.y - (top + height)) < margin);

          for (const [key, value] of Object.entries(this.controls)) {
            const isRow = key.includes('row');
            if (key.includes('rowSize') || key.includes('colSize')) {
              const control = value as TableVectorResizeControl;

              if (isRow) {
                control.shouldHide = !isOnHorizontalEdge;
                control.cursorStyle = isOnHorizontalEdge
                  ? 'row-resize'
                  : 'move';
              } else {
                control.shouldHide = !isOnVerticalEdge;
                control.cursorStyle = isOnVerticalEdge ? 'col-resize' : 'move';
              }
            }
          }
        } else {
          for (const [key, value] of Object.entries(this.controls)) {
            if (key.includes('rowSize') || key.includes('colSize')) {
              const control = value as TableVectorResizeControl;

              control.shouldHide = false;
              control.cursorStyle = key.includes('row')
                ? 'row-resize'
                : 'col-resize';
            }
          }
        }
      }
    }
  }

  _objectsInIntersectingMergedCells(
    index: number,
    isRow = true
  ): fabric.Object[] {
    const objects = Array<fabric.Object>();

    for (const cell of this.mergedCells) {
      const [start, end] = cell;
      const [startRow, startCol] = start;
      const [endRow, endCol] = end;

      // Only get objects in mergedCells that intersect this vector
      if (
        (isRow && index >= startRow && index < endRow) ||
        (index >= startCol && index < endCol)
      ) {
        const dimensions = this._getCellDimensions(...start);
        if (dimensions != null) {
          objects.push(...this._objectsInDimensions(dimensions));
        }
      }
    }

    return objects;
  }

  _vectorIntersectsAnyMergedCell(
    index: number,
    isRow = true,
    startInclusive = true,
    endInclusive = true
  ): boolean {
    for (const cell of this.mergedCells) {
      if (
        this._vectorIntersectsMergedCell(
          index,
          isRow,
          cell,
          startInclusive,
          endInclusive
        )
      ) {
        return true;
      }
    }
    return false;
  }

  _vectorIntersectsMergedCell(
    index: number,
    isRow = true,
    mergedCell: MergedCell,
    startInclusive = true,
    endInclusive = true
  ): boolean {
    const [start, end] = mergedCell;
    const [startRow, startCol] = start;
    const [endRow, endCol] = end;

    if (startInclusive && endInclusive) {
      return (
        (isRow && index >= startRow && index <= endRow) ||
        (!isRow && index >= startCol && index <= endCol)
      );
    }

    if (startInclusive && !endInclusive) {
      return (
        (isRow && index > startRow && index <= endRow) ||
        (!isRow && index > startCol && index <= endCol)
      );
    }

    if (!startInclusive && endInclusive) {
      return (
        (isRow && index >= startRow && index < endRow) ||
        (!isRow && index >= startCol && index < endCol)
      );
    }

    if (!startInclusive && !endInclusive) {
      return (
        (isRow && index > startRow && index < endRow) ||
        (!isRow && index > startCol && index < endCol)
      );
    }

    return false;
  }

  private vectorIntersectsPerpendicularMergedCell(
    index: number,
    isRow = true,
    cell: MergedCell
  ): boolean {
    if (this._vectorIntersectsMergedCell(index, isRow, cell, true, true)) {
      // If this merged cell spans more than one row or column (depending on
      // isRow), then it is not inline
      return isRow ? cell[0][0] !== cell[1][0] : cell[0][1] !== cell[1][1];
    }
    return false;
  }

  private vectorIntersectsAnyPerpendicularMergedCell(index, isRow) {
    for (const cell of this.mergedCells) {
      if (this.vectorIntersectsPerpendicularMergedCell(index, isRow, cell)) {
        return true;
      }
    }
    return false;
  }

  // Find merged cell with smallest available space between end of cell and end
  // of farthest right/down object
  _getMinMergedCellSpace(index: number, isRow = true): number | null {
    let minDiff = Number.MAX_SAFE_INTEGER;

    for (const cell of this.mergedCells) {
      const [start] = cell;

      if (this._vectorIntersectsMergedCell(index, isRow, cell)) {
        const dimensions = this._getCellDimensions(...start);

        if (!dimensions) continue;

        const cellEnd = isRow
          ? dimensions?.top + dimensions?.height
          : dimensions?.left + dimensions?.width;

        for (let i = 0; i < this._containedObjects.length; i++) {
          const object = this._containedObjects[i];

          if (this._containsObject(dimensions, object)) {
            const objectEnd = isRow
              ? object.top + object.getScaledHeight()
              : object.left + object.getScaledWidth();

            minDiff = Math.min(minDiff, cellEnd - objectEnd);
          }
        }
      }
    }

    return minDiff === Number.MAX_SAFE_INTEGER ? null : minDiff;
  }

  // Find vector with smallest available space between end of cell and end of
  // farthest right/down object
  _getMinVectorSpace(index: number, isRow = true): number | null {
    let vectorObjects = this.objectsInVector(index, isRow);
    if (vectorObjects.length === 0) {
      return null;
    }

    const rect = this.getVectorRect({ index, isRow, scaled: true });
    if (!rect) {
      return null;
    }

    const vectorEnd = isRow ? rect.top + rect.height : rect.left + rect.width;

    const mergedCellObjects = this._objectsInIntersectingMergedCells(
      index,
      isRow
    );
    // Dont include merged cell objects in vector objects
    vectorObjects = vectorObjects.filter(
      obj => !mergedCellObjects.includes(obj)
    );
    const vectorObjectsBounds = this.objectsBoundingBox(vectorObjects);

    if (vectorObjectsBounds == null) {
      return null;
    }

    const vectorObjectsEnd = isRow
      ? vectorObjectsBounds.bottom
      : vectorObjectsBounds.right;

    return vectorEnd - vectorObjectsEnd;
  }

  _getMinTextSpace(index: number, isRow = true): number | null {
    // Only care about height changes for text
    if (!isRow) return null;

    let minDiff = Number.MAX_SAFE_INTEGER;

    for (const cell of this.mergedCells) {
      const [start] = cell;

      if (this._vectorIntersectsMergedCell(index, isRow, cell)) {
        const dimensions = this._getCellDimensions(...start);

        if (!dimensions) continue;

        const { top, height } = dimensions;
        const cellEnd = top + height;

        Object.entries(this._textFields).forEach(([key, field]) => {
          const [row] = this._parseCellKey(key);
          const { textContent } = field;
          // We only care about text fields in the index row
          if (row !== index) return;

          if (!textContent || textContent.length === 0) return;

          const textHeight = field.textDimensions?.height ?? 0;
          minDiff = Math.min(minDiff, cellEnd - (top + textHeight));
        });
      }
    }

    return minDiff === Number.MAX_SAFE_INTEGER ? null : minDiff;
  }

  minSpaceAvailable(index: number, isRow = true): number | null {
    const vectorSpace =
      this._getMinVectorSpace(index, isRow) ?? Number.MAX_SAFE_INTEGER;
    const mergedCellSpace =
      this._getMinMergedCellSpace(index, isRow) ?? Number.MAX_SAFE_INTEGER;
    const textSpace =
      this._getMinTextSpace(index, isRow) ?? Number.MAX_SAFE_INTEGER;

    return Math.min(vectorSpace, mergedCellSpace, textSpace);
  }

  _containsObject(dimensions, object): boolean {
    const center = object.getCenterPoint();
    const { left, top, width, height } = dimensions;
    if (
      center.x > left &&
      center.y > top &&
      center.x < left + width &&
      center.y < top + height
    ) {
      return true;
    }
    return false;
  }

  _getCellDimensionsFromPointer(
    point: {
      x: number;
      y: number;
    },
    includeMergedCells = true
  ): CellDimensions | null {
    const { numColumns, numRows } = this;

    for (let i = 0; i < numRows; i++) {
      for (let j = 0; j < numColumns; j++) {
        const cell = this._getCellDimensions(i, j, includeMergedCells);
        if (cell == null) {
          continue;
        }
        const { left, top, width, height } = cell;
        if (this._cellContainsPoint(point, left, top, width, height)) {
          return cell;
        }
      }
    }
    return null;
  }

  _cellContainsPoint(
    point: { x: number; y: number },
    x: number,
    y: number,
    width: number,
    height: number
  ): boolean {
    return (
      point.x > x && point.y > y && point.x < x + width && point.y < y + height
    );
  }

  _getUnmergedCellDimensions(
    row: number,
    column: number,
    scaled = false
  ): CellDimensions | null {
    if (
      row < 0 ||
      row > this.numRows - 1 ||
      column < 0 ||
      column > this.numColumns - 1
    ) {
      return null;
    }

    let scale = scaled ? this.scaleY ?? 1 : 1;
    const height = this[this.getVectorDimensionKey(row, true)] * scale;

    let left = this.left;
    let top = this.top;
    // Add up vector heights to get top of given cell
    for (let i = 0; i < row; i++) {
      top += this[this.getVectorDimensionKey(i, true)] * scale;
    }

    scale = scaled ? this.scaleX ?? 1 : 1;
    const width = this[this.getVectorDimensionKey(column, false)] * scale;

    // Add up vector width to get left of given cell
    for (let i = 0; i < column; i++) {
      left += this[this.getVectorDimensionKey(i, false)] * scale;
    }

    return { left, top, width, height, row, column };
  }

  // Returns the dimensions of a cell, plus row/column number.
  _getCellDimensions(
    row: number,
    column: number,
    includeMergedCells = true
  ): CellDimensions | null {
    if (
      row < 0 ||
      row > this.numRows - 1 ||
      column < 0 ||
      column > this.numColumns - 1
    ) {
      return null;
    }

    if (includeMergedCells && this._inMergedCell([row, column])) {
      // If we are in a merged cell, get all the cells in that merged cell,
      // add up there dimensions, and push as one cell into the cells array
      const mergedCell = this._getMergedCell([row, column]);
      const [start, end] = mergedCell;
      const startCell = this._getUnmergedCellDimensions(
        start[0],
        start[1],
        true
      );

      const endCell = this._getUnmergedCellDimensions(end[0], end[1], true);
      if (startCell === null || endCell === null) {
        return null;
      }
      return {
        left: startCell.left,
        top: startCell.top,
        width: endCell.left + endCell.width - startCell.left,
        height: endCell.top + endCell.height - startCell.top,
        row,
        column,
      };
    } else {
      return this._getUnmergedCellDimensions(row, column, true);
    }
  }

  _getAllDrawnCells(): CellDimensions[] {
    const cells: CellDimensions[] = [];
    for (let i = 0; i < this.numRows; i++) {
      for (let j = 0; j < this.numColumns; j++) {
        const merged = this._getMergedCell([i, j]);
        if ((merged && this._cellEquals(merged[0], [i, j])) || !merged) {
          const cell = this._getCellDimensions(i, j);
          if (cell) {
            cells.push(cell);
          }
        }
      }
    }
    return cells;
  }

  // Compile cells for multiselect. use the lowest/highest rows and columns
  // as the boundary for cells to highlight
  _getSelectedCells(): Array<CellDimensions | null> {
    // This is only being called when guarded with hasSelectedCell
    const [row, column] = this.currentCell!;
    if (this.hasMultipleSelectedCells()) {
      const cells = Array<CellDimensions | null>();
      const [prevRow, prevColumn] = this.previousCell;
      const rowMin = Math.min(row, prevRow);
      const rowMax = Math.max(row, prevRow);
      const columnMin = Math.min(column, prevColumn);
      const columnMax = Math.max(column, prevColumn);

      for (let i = rowMin; i <= rowMax; i++) {
        for (let j = columnMin; j <= columnMax; j++) {
          cells.push(this._getCellDimensions(i, j));
        }
      }
      return cells;
    }
    return [this._getCellDimensions(row, column)];
  }

  _selectCell(event: fabric.IEvent<Event>): void {
    if (this.locked) return;

    // Allow the user to interact with the table when cell(s) selected, rather
    // than moving the table. Does not affect collaborators
    // this.lockMovement();

    // Save currently selected cell before modifier key pressed so that cell
    // stays the same while modifier key is pressed
    if (!this.shouldMultiSelectCell) {
      this.previousCell = this.currentCell;
    }
    const { pointer } = event;

    // @ts-expect-error Transform does define shiftKey
    this.shouldMultiSelectCell = event.transform?.shiftKey;

    const canvasPoint = this.canvas.pointToCanvasCoords(pointer);
    const cell = this._getCellDimensionsFromPointer(canvasPoint, false);
    if (cell != null) {
      const { row, column } = cell;
      this.currentCell = [row, column];
      this._useCellFillValues(row, column);
      this.controller.actionEnableSelectionKeys();
      this.controller.actionRerenderToolbar();
      this.allowsDragSelection = true;
      this.hoverCursor = 'default';
      this.canvas.requestRenderAll();
    } else {
      this.allowsDragSelection = false;
      this.clearSelectedCells();
    }
  }

  clearSelectedCells() {
    this._restoreFillValues();
    this.currentCell = null;
    this.previousCell = null;
    this.shouldMultiSelectCell = false;
    this.controller.actionEnableToolKeys();
    this.activeTextField?.endTextEditing();
    this.activeTextField = null;
    this.allowsDragSelection = false;
    this.canvas?.requestRenderAll();
  }

  _selectNextCell(isRow = true, allowWrap = false, forwards = true): void {
    let [r, c] = this.currentCell!;
    const inMergedCell = this._inMergedCell([r, c]);
    if (inMergedCell) {
      const mergedCell = this._getMergedCell([r, c]);
      const [[r1, c1], [r2, c2]] = mergedCell;

      // If moving along the row, maintain the row and chose the end column of
      // the merged cell. Reverse the logic for columns
      r = forwards ? (isRow ? r : r2) : isRow ? r : r1;
      c = forwards ? (isRow ? c2 : c) : isRow ? c1 : c;
    }
    if (allowWrap && isRow) {
      const withinTable = forwards
        ? r < this.numRows - 1 || c < this.numColumns - 1
        : r > 0 || c > 0;
      if (withinTable) {
        const next: Cell = forwards
          ? c === this.numColumns - 1
            ? [r + 1, 0]
            : [r, c + 1]
          : c === 0
          ? [r - 1, this.numColumns - 1]
          : [r, c - 1];
        this.currentCell = next;
      }
      this.canvas.requestRenderAll();
    } else {
      const withinTable = isRow
        ? forwards
          ? c < this.numColumns - 1
          : c > 0
        : forwards
        ? r < this.numRows - 1
        : r > 0;

      if (withinTable) {
        const next: Cell = isRow
          ? forwards
            ? [r, c + 1]
            : [r, c - 1]
          : forwards
          ? [r + 1, c]
          : [r - 1, c];
        this.currentCell = next;
      }
      this.canvas.requestRenderAll();
    }
  }

  _setAllCellsProp(key: string, value) {
    if (!this.cellProperties.includes(key)) {
      return;
    }
    const cells = this._getTableCellsCoords();
    for (const [row, col] of cells) {
      this.set(this._getCellKey(row, col, key), value);
    }
  }

  containsPoint(pointer, lines, absolute, calculate) {
    const { left, top } = this;
    const adjustedPointer = this.canvas.restorePointerVpt(pointer);
    const zoom = this.canvas.getZoom();
    const radius = SELECTION_DISTANCE / zoom;
    const coords = this.getCoords(true);

    let contains = pointInRect(adjustedPointer, {
      left,
      top,
      width: this.getScaledWidth(),
      height: this.getScaledHeight(),
    });
    contains ||= this.containsPointInText(pointer);

    // Only consider top and left sides for drag control
    let showDragControls = pointNearLine(
      adjustedPointer,
      coords[0],
      coords[1],
      radius
    );
    showDragControls ||= pointNearLine(
      adjustedPointer,
      coords[3],
      coords[0],
      radius
    );

    if (showDragControls) {
      this._showVectorDragControls(adjustedPointer);
    }

    contains ||= super.containsPoint(pointer, lines, absolute, calculate);

    return contains;
  }

  containsPointInText(pointer: fabric.Point): boolean {
    const adjustedPointer = this.canvas.restorePointerVpt(pointer);
    const zoom = this.canvas.getZoom();

    // Get text position
    const pos = this.textPosition();

    const zoomScale = this.getLabelZoomScale(zoom);
    const adjustedPos = {
      left: pos.left,
      top: pos.top,
      width: (pos.width * zoomScale) / zoom,
      height: (pos.height * zoomScale) / zoom,
    };

    return pointInRect(adjustedPointer, adjustedPos);
  }

  // We have to override this so that a frame that is completely zoomed in
  // doesn't disappear because it doesn't contain the center point.
  _containsCenterOfCanvas(pointTL, pointBR, calculate) {
    // worst case scenario the object is so big that contains the screen
    const centerPoint = {
      x: (pointTL.x + pointBR.x) / 2,
      y: (pointTL.y + pointBR.y) / 2,
    };
    if (
      super.containsPoint(centerPoint as fabric.Point, null, true, calculate)
    ) {
      return true;
    }
    return false;
  }

  _updateVectorSizeControls(key: 'numRows' | 'numColumns', value) {
    const isRow = key === 'numRows';
    if (this[key] < value) {
      // Add a resize control to the last vector
      const index = value - 1;
      const controlKey = this._getVectorResizeControlKey(index, isRow);
      this.controls[controlKey] = new TableVectorResizeControl(index, isRow);
    } else {
      // Delete resize control for last vector
      const controlKey = this._getVectorResizeControlKey(value, isRow);
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this.controls[controlKey];
    }
  }

  _drawDragVector(ctx: CanvasRenderingContext2D) {
    const corner = this.__corner;
    if (!corner || typeof corner !== 'string') {
      // This should not happen as there must be a corner while dragging, but
      // (most likely) collab ops can remove the currently dragged obj. Early out
      // here to avoid errors
      return;
    }
    const { draggedVectorLeft, draggedVectorTop } = this;
    const [index, isRow] = this._parseVectorControl(corner);
    const rect = this.getVectorRect({ index, isRow });

    if (rect) {
      const { width, height } = rect;
      ctx.save();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#0073CF';
      ctx.beginPath();
      if (draggedVectorLeft) {
        ctx.rect(draggedVectorLeft, 0 - height / 2, width, height);
      } else if (draggedVectorTop) {
        ctx.rect(0 - width / 2, draggedVectorTop, width, height);
      }
      this._renderStroke(ctx);
      ctx.closePath();
      ctx.restore();
    }

    const vectorCache = this.getVectorRectsTableRelative(isRow);
    const adjustedPointer = fabric.util.transformPoint(
      { x: this.left, y: this.top } as fabric.Point,
      fabric.util.invertTransform(this.calcOwnMatrix())
    );

    let left, top;

    if (this.draggedVectorEndIndex === 0) {
      left = adjustedPointer.x;
      top = adjustedPointer.y;
    } else if (this.draggedVectorEndIndex) {
      left = isRow
        ? adjustedPointer.x
        : vectorCache[this.draggedVectorEndIndex];
      top = isRow ? vectorCache[this.draggedVectorEndIndex] : adjustedPointer.y;
    }

    ctx.save();
    ctx.fillStyle = '#0073CF';
    const indicatorWidth = 4;

    // Draw the destination indicator the dragged item
    ctx.fillRect(
      left,
      top,
      isRow ? this.width : indicatorWidth,
      isRow ? indicatorWidth : this.height
    );
    ctx.restore();
  }

  getVectorRectsTableRelative(isRow = true) {
    const adjustedPointer = fabric.util.transformPoint(
      { x: this.left, y: this.top } as fabric.Point,
      fabric.util.invertTransform(this.calcOwnMatrix())
    );
    const firstValue = isRow ? adjustedPointer.y : adjustedPointer.x;
    const array = [firstValue];
    if (isRow) {
      let top = Math.floor(adjustedPointer.y);
      for (let i = 0; i < this.numRows; i++) {
        const rowHeight = this[this.getVectorDimensionKey(i, true)];
        top += Math.floor(rowHeight);
        array[i + 1] = top;
      }
    } else {
      let left = Math.floor(adjustedPointer.x);
      for (let i = 0; i < this.numColumns; i++) {
        const colWidth = this[this.getVectorDimensionKey(i, false)];
        left += Math.floor(colWidth);
        array[i + 1] = left;
      }
    }

    return array;
  }

  getEndVectorIndex(vectorMidPoint: number, vectorIndex: number, isRow) {
    const vectorLocalLeftTop = this.getVectorRectsTableRelative(isRow);
    const [left, right] = binSearch(
      vectorLocalLeftTop,
      Math.floor(vectorMidPoint)
    );
    if (vectorIndex >= right) {
      return right;
    } else {
      return left;
    }
  }

  _parseVectorControl(corner: string): [number, boolean] {
    if (!corner || typeof corner !== 'string') {
      return [-1, false];
    }
    const isRow = corner[0] === 'r';
    const split = corner.split('_');
    const index = parseInt(split[split.length - 1], 10);
    return [index, isRow];
  }

  // Key for given vector height/width
  getVectorDimensionKey(index: number, isRow = true): keyof Table {
    const key = isRow ? `row_${index}_height` : `col_${index}_width`;
    return key as keyof Table;
  }

  _isVectorDimensionKey(keyOrPath: KeyPath): boolean {
    return (
      (keyOrPath.includes('row_') || keyOrPath.includes('col_')) &&
      (keyOrPath.includes('height') || keyOrPath.includes('width'))
    );
  }

  _setCellProp(row: number, column: number, key: string, value) {
    this.set(this._getCellKey(row, column, key), value);
  }

  _getCellKey(row: number, column: number, key = '') {
    let cellKey = `cell_${row}_${column}`;
    if (key !== '') cellKey += `_${key}`;
    return cellKey;
  }

  _getCellValue(row: number, column: number, key = '') {
    const cellKey = this._getCellKey(row, column, key);
    return cellKey in this ? this[cellKey] : undefined;
  }

  _getCellOpacity(row: number, column: number): number {
    return (
      this[this._getCellKey(row, column, 'opacity')] ||
      this.tableOpacity ||
      this.opacity ||
      1
    );
  }

  _parseCellKey(key: string): [row: number, column: number, prop: string] {
    const [, row, column, prop] = key.split('_');
    return [parseInt(row, 10), parseInt(column, 10), prop];
  }

  _getMergedCellKey(cell: MergedCell): keyof this {
    const [start, end] = cell;
    const [r0, c0] = start;
    const [r1, c1] = end;
    return `merged_cell_${r0}_${c0}_${r1}_${c1}` as keyof this;
  }

  _parseMergedCellKey(key: string): MergedCell {
    const [, , r0, c0, r1, c1] = key.split('_');
    return [
      [parseInt(r0, 10), parseInt(c0, 10)],
      [parseInt(r1, 10), parseInt(c1, 10)],
    ];
  }

  // Key for given vector resize control
  _getVectorResizeControlKey(index: number, isRow = true): string {
    return isRow ? `rowSize_${index}` : `colSize_${index}`;
  }

  getVectorRect({ index, isRow = true, scaled = false }): Rect | undefined {
    const scaleX = scaled ? this.scaleX : 1;
    const scaleY = scaled ? this.scaleY : 1;
    let start = isRow ? this.top : this.left;
    const count = isRow ? this.numRows : this.numColumns;
    for (let i = 0; i < count; i++) {
      const vectorDimension = this[this.getVectorDimensionKey(i, isRow)];
      if (i === index) {
        return {
          left: isRow ? this.left : start,
          top: isRow ? start : this.top,
          width: isRow ? this.width * scaleX : vectorDimension * scaleX,
          height: isRow ? vectorDimension * scaleY : this.height * scaleY,
        };
      }
      start += vectorDimension * (isRow ? scaleY : scaleX);
    }
  }

  _getVectorCellsCoords(index: number, isRow = true): Cell[] {
    const cells: Cell[] = [];
    const vectorCount = isRow ? this.numColumns : this.numRows;
    for (let i = 0; i < vectorCount; i++) {
      const row = isRow ? index : i;
      const column = isRow ? i : index;
      cells.push([row, column]);
    }
    return cells;
  }

  _getTableCellsCoords(): Cell[] {
    const cells: Cell[] = [];
    for (let i = 0; i < this.numRows; i++) {
      cells.push(...this._getVectorCellsCoords(i, true));
    }
    return cells;
  }

  // Update the order of rows/columns resulting from dragging a row/column
  updateVectorOrder(start: number, end: number, isRow = true): boolean {
    if (
      start === null ||
      end === null ||
      end < 0 ||
      start < 0 ||
      end === start
    ) {
      console.debug(`Invalid start end: ${start} ${end}`);
      return false;
    }
    // Check if dragging forward (right/down) or backward (left/up)
    const isForwardsDrag = end > start;

    // Don't allow vector drags within a merged cell.
    if (
      this._vectorIntersectsAnyMergedCell(start, isRow) ||
      this._vectorIntersectsAnyMergedCell(
        end,
        isRow,
        !isForwardsDrag,
        isForwardsDrag
      )
    ) {
      this.controller.actionShowMessage(
        `Unmerge cells before moving ${isRow ? 'row' : 'column'}`
      );
      return false;
    }

    const draggedVectorDimKey = this.getVectorDimensionKey(start, isRow);
    const draggedVectorDim = this[draggedVectorDimKey];
    const endVectorDimKey = this.getVectorDimensionKey(end, isRow);
    const draggedVectorLeftTop = this.getVectorRect({
      index: start,
      isRow,
      scaled: true,
    });
    // Grab all objects in dragged vector
    const objectsInDraggedVector = this.objectsInVector(start, isRow);
    const cellsPropsInDraggedVector = this._getVectorCellsProperties(
      start,
      isRow
    );
    const objectsToUpdate: fabric.Object[] = [];
    // Get indices for intermediate vectors
    const loopStart = end > start ? start : end;
    const loopEnd = end > start ? end : start;

    // Grab all objects in intermediate vectors
    for (let i = loopStart; i <= loopEnd; i++) {
      if (i !== start) {
        objectsToUpdate.push(...this.objectsInVector(i, isRow));
      }
    }

    if (isForwardsDrag) {
      for (let i = start; i < end; i++) {
        const key = this.getVectorDimensionKey(i, isRow);
        const key2 = this.getVectorDimensionKey(i + 1, isRow);
        this.set(key, this[key2]);
        this._shiftVectorCells(i, isRow, !isForwardsDrag);
      }
    } else {
      for (let i = start; i > end; i--) {
        const key = this.getVectorDimensionKey(i, isRow);
        const key2 = this.getVectorDimensionKey(i - 1, isRow);
        this.set(key, this[key2]);
        this._shiftVectorCells(i, isRow, !isForwardsDrag);
      }
    }

    // Swap dragged and destination vector dimensions
    this.set(endVectorDimKey, draggedVectorDim);

    // Apply the cell properties from the start vector to the end vector
    this._applyVectorCellsProperties(end, cellsPropsInDraggedVector, isRow);

    // Move objects in dragged vector to end vector
    objectsInDraggedVector.forEach(obj => {
      const leftTop = isRow ? 'top' : 'left';
      const rect = this.getVectorRect({ index: end, isRow, scaled: true });
      if (rect && draggedVectorLeftTop) {
        const vectorStart = isRow ? rect.top : rect.left;
        const diffValue = isRow
          ? draggedVectorLeftTop.top
          : draggedVectorLeftTop.left;
        const diff = vectorStart - diffValue;
        if (isSimpleGroup(obj)) {
          const left = isRow ? obj.left : obj[leftTop] + diff;
          const top = isRow ? obj[leftTop] + diff : obj.top;
          obj.moveToCoords({ left, top });
        } else {
          obj.set(leftTop, obj[leftTop] + diff);
          obj.positionConnectors?.();
        }
      }
    });

    // Shift intermediate objects
    objectsToUpdate.forEach(obj => {
      const leftTop = isRow ? 'top' : 'left';
      const scale = isRow ? this.scaleY : this.scaleX;
      const diff = draggedVectorDim * scale;
      const adjusted = end > start ? -diff : diff;
      if (isSimpleGroup(obj)) {
        const left = isRow ? obj.left : obj[leftTop] + adjusted;
        const top = isRow ? obj[leftTop] + adjusted : obj.top;
        obj.moveToCoords({ left, top });
      } else {
        obj.set(leftTop, obj[leftTop] + adjusted);
        obj.positionConnectors?.();
      }
    });

    this._updateMergedCellsForVectorDrag(start, end, isRow);

    this.updateTextFieldDimensions();

    trackEvent(
      'Feature-7176742954015814903',
      'User moves whiteboard table cells'
    );
    return true;
  }

  // Debug function, draws cells at center of canvas, which is helpful if you'd
  // like to still see how the grid behaves
  _drawGrid(ctx: CanvasRenderingContext2D) {
    let left = 0;
    let top = 0;
    const cells: {
      left: number;
      top: number;
      width: number;
      height: number;
    }[] = [];
    for (let i = 0; i < this.numRows; i++) {
      const cellHeight = this[this.getVectorDimensionKey(i, true)];
      for (let j = 0; j < this.numColumns; j++) {
        const cellWidth = this[this.getVectorDimensionKey(j, false)];
        const adjustedPointer = fabric.util.transformPoint(
          { x: left, y: top } as fabric.Point,
          fabric.util.invertTransform(this.calcOwnMatrix())
        );
        cells.push({
          left: adjustedPointer.x,
          top: adjustedPointer.y,
          width: cellWidth,
          height: cellHeight,
        });
        left += cellWidth * this.scaleX;
      }
      top += cellHeight * this.scaleY;
      left = 0;
    }
    ctx.save();
    ctx.fillStyle = 'red';

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      ctx.fillStyle = ctx.fillStyle === '#ff0000' ? 'green' : 'red';
      ctx.fillRect(cell.left, cell.top, cell.width, cell.height);
    }
    ctx.restore();
  }

  _drawCells(ctx: CanvasRenderingContext2D): void {
    this._saveStrokeStyles();

    ctx.save();
    const { opacity, tableOpacity } = this;
    ctx.strokeStyle = this.stroke ?? '#999999';
    ctx.globalAlpha = tableOpacity ?? opacity ?? 1;

    ctx.beginPath();
    for (const cell of this._getAllDrawnCells()) {
      const { x, y } = fabric.util.transformPoint(
        { x: cell.left, y: cell.top } as fabric.Point,
        fabric.util.invertTransform(this.calcOwnMatrix())
      );
      ctx.rect(
        x + this.strokeWidth / 2,
        y + this.strokeWidth / 2,
        cell.width,
        cell.height
      );
    }
    this._renderStroke(ctx);
    ctx.closePath();
    ctx.restore();

    this._restoreStrokeStyles();
  }

  private highlightTable(ctx: CanvasRenderingContext2D) {
    if (
      this.canvas.hasGroupSelector &&
      this.canvas.groupSelectorContainsObject(this) &&
      !this.canvas.groupSelectorIntersectsOther(this)
    ) {
      const width = this.getScaledWidth();
      const height = this.getScaledHeight();
      ctx.save();
      ctx.strokeStyle = HOVER_SHADOW_COLOR;
      ctx.strokeRect(-width / 2, -height / 2, width, height);
      ctx.restore();
    }
  }

  private highlightSelectedCells(ctx: CanvasRenderingContext2D) {
    if (this.hasSelectedCell()) {
      const cells = this._getSelectedCells();
      if (cells != null) {
        cells.forEach(cell => {
          if (cell != null) {
            this.highlightCell(ctx, cell);
          }
        });
      }
    }
  }

  private highlightCell(
    ctx: CanvasRenderingContext2D,
    cell: CellDimensions
  ): void {
    const { left, top, width, height } = cell;
    const scaleX = this.scaleX ?? 1;
    const scaleY = this.scaleY ?? 1;
    const borderWidth = this.strokeWidth;
    const zoom = this.canvas?.getZoom() || 1;
    const { x, y } = fabric.util.transformPoint(
      { x: left, y: top } as Point,
      fabric.util.invertTransform(this.calcOwnMatrix())
    );

    this._saveStrokeStyles();

    ctx.save();
    const { opacity, tableOpacity } = this;
    this.stroke = '#0073CF';
    this.strokeWidth = 2 / zoom;
    ctx.globalAlpha = tableOpacity ?? opacity ?? 1;
    ctx.beginPath();
    ctx.rect(
      x + borderWidth,
      y + borderWidth,
      width / scaleX - borderWidth,
      height / scaleY - borderWidth
    );
    ctx.closePath();
    this._renderStroke(ctx);
    ctx.restore();

    this._restoreStrokeStyles();
  }

  private getCellFillAndOpacity(
    row: number,
    column: number
  ): { fill: string; opacity: number } {
    const isHeaderCell =
      (this.hasHeaderRow && row === 0) ||
      (this.hasHeaderColumn && column === 0);

    if (isHeaderCell) {
      return { fill: HEADER_FILL_COLOR, opacity: 1 };
    }

    const isAlternateRowCell =
      // 'transparent' is how a user turns alternate row colouring off
      this.alternatingRowColor !== 'transparent' &&
      /* If there's no header row then alternates should be on odd-indexed rows so that it goes
      [normal, alternate, normal, alternate, ...]
      but if there is a header then alternates go on even indexes so that it goes
      [header, normal, alternate, normal, alternate, ...] */
      row % 2 === (this.hasHeaderRow ? 0 : 1);

    if (isAlternateRowCell) {
      return {
        fill: this.alternatingRowColor,
        opacity: this.alternatingRowOpacity,
      };
    }

    const cellFill = this._getCellValue(row, column, 'fill');
    if (typeof cellFill === 'string') {
      return {
        fill: cellFill,
        opacity: this._getCellValue(row, column, 'opacity') ?? 1,
      };
    }

    if (this.tableFillColor) {
      return {
        fill: this.tableFillColor,
        opacity: this.tableOpacity ?? 1,
      };
    }

    if (typeof this.fill !== 'string') {
      // 'fill' is in the defaults for a table (see DefaultsManager) so it should be
      // set from the moment a table is created. If code is sidestepping the defaults manager
      // then it should probably be changed to use it. This function's job isn't to provide
      // a default so we throw if somehow there is no color set anywhere.
      throw new Error('Fill should always be defined on a table');
    }

    return { fill: this.fill, opacity: this.opacity ?? 1 };
  }

  _fillCells(ctx: CanvasRenderingContext2D): void {
    const cells = this._getAllDrawnCells();
    for (const cell of cells || []) {
      const { left, top, width, height, row, column } = cell;

      const { fill, opacity } = this.getCellFillAndOpacity(row, column);

      const { x, y } = fabric.util.transformPoint(
        { x: left, y: top } as Point,
        fabric.util.invertTransform(this.calcOwnMatrix())
      );

      ctx.save();
      ctx.fillStyle = fill;
      ctx.globalAlpha = opacity;
      ctx.fillRect(
        x + this.strokeWidth / 2,
        y + this.strokeWidth / 2,
        width / this.scaleX,
        height / this.scaleY
      );
      ctx.restore();
    }
  }

  _drawHoveredCells(ctx: CanvasRenderingContext2D): void {
    if (this.hovered) {
      this.hoveredPoints!.forEach(point => {
        const cell = this._getCellDimensionsFromPointer(point);
        if (cell !== null) {
          this.highlightCell(ctx, cell);
        }
      });
    }
  }

  _drawResizeVector(ctx: CanvasRenderingContext2D) {
    if (!this.__corner || typeof this.__corner !== 'string') {
      return;
    }
    const [index, isRow] = this._parseVectorControl(this.__corner);
    const rect = this.getVectorRect({
      index,
      isRow,
      scaled: true,
    });
    if (rect) {
      const { left, top, width, height } = rect;
      const adjustedPointer = fabric.util.transformPoint(
        { x: left, y: top } as fabric.Point,
        fabric.util.invertTransform(this.calcOwnMatrix())
      );

      ctx.save();
      ctx.fillStyle = '#E0EEF9';
      ctx.fillRect(
        adjustedPointer.x,
        adjustedPointer.y,
        width / this.scaleX,
        height / this.scaleY
      );
      ctx.restore();
    }
  }

  // TODO round corners for table and border
  _render(ctx: CanvasRenderingContext2D) {
    // DEBUG
    // this._drawGrid(ctx);

    this._fillCells(ctx);
    this._drawCells(ctx);

    this._drawDragVector(ctx);
    this.highlightTable(ctx);
    this.highlightSelectedCells(ctx);
    this._drawHoveredCells(ctx);
    const previousCell = this.currentCell
      ? ([...this.currentCell] as Cell)
      : null;
    this.renderText(ctx, true);
    this.currentCell = previousCell;

    this.renderTitle(ctx);
  }

  private prevStrokeWidth?: number;

  _saveStrokeStyles() {
    this.prevStrokeWidth = this.strokeWidth;
  }

  _restoreStrokeStyles() {
    this.strokeWidth = this.prevStrokeWidth ?? 1;
  }

  _useCellFillValues(row: number, column: number) {
    this._saveFillValues();
    this.fill =
      this[this._getCellKey(row, column, 'fill')] || this.tableFillColor;
    this.opacity = this._getCellOpacity(row, column);
  }

  _saveFillValues(): void {
    if (!this.tableFillColor) {
      this.tableFillColor = this.fill;
      this.tableOpacity = this.opacity;
    }
  }

  _restoreFillValues() {
    if (this.tableFillColor) {
      this.fill = this.tableFillColor;
      this.opacity = this.tableOpacity;
    }
    this.tableFillColor = undefined;
    this.tableOpacity = undefined;
  }

  _vectorAvailableSpace(index: number, isRow = true, objects: fabric.Object[]) {
    const key = this.getVectorDimensionKey(index, isRow);
    const minDimension = isRow ? MIN_ROW_HEIGHT : MIN_COLUMN_WIDTH;
    const bounds = this.vectorObjectsBoundingBox(index, isRow, objects);
    if (!bounds) {
      return Math.max(this[key] - minDimension, 0);
    }
    const rect = this.getVectorRect({ index, isRow, scaled: true });
    if (rect) {
      const { left, top, width, height } = rect;
      const boudnsMin = isRow
        ? top + height - bounds.bottom
        : left + width - bounds.right;
      return Math.max(boudnsMin, 0);
    }
    return 0;
  }

  handle2DResize(newHeight: number, newWidth: number, corner) {
    const rowObjects = this.objectsInVectors(true);
    const colObjects = this.objectsInVectors(false);
    const width = this._resizeDimension(newWidth, false, corner, colObjects);
    const height = this._resizeDimension(newHeight, true, corner, rowObjects);
    this.set('width', width);
    this.set('height', height);
    return height || width;
  }

  handleResize(newDimension: number, isRow = true, corner) {
    const tableDimKey = isRow ? 'height' : 'width';
    const oldDimension = this[tableDimKey];
    const objects = this.objectsInVectors(isRow);
    const dimension = this._resizeDimension(
      newDimension,
      isRow,
      corner,
      objects
    );
    if (oldDimension !== dimension) {
      this.set(tableDimKey, dimension);
    }
    return oldDimension !== dimension;
  }

  toggleResizing(isResizing = false) {
    this.hasControls = !isResizing;
  }

  createTextField(row: number, column: number) {
    const cellDimensions = this._getCellDimensions(row, column);

    if (cellDimensions != null) {
      // Adjust coordinates to be relative to parent
      cellDimensions.left -= this.left;
      cellDimensions.top -= this.top;
      const { left, top, width, height } = cellDimensions;
      // Key for tracking this field within textFields
      const textFieldKey = this._getCellKey(row, column);
      const textFieldOptions = this.valueAtPath(['textFields', textFieldKey]);

      const textField = new TextField(
        this,
        { left, top, width, height },
        {
          ...(textFieldOptions ?? {}),
          key: textFieldKey,
          textProps: this.cellTextProps,
          calcTextMargin: () => ({ left: 10, top: 10, right: 10, bottom: 10 }),
          textClass: () => null,
          controller: this.controller,
          canvas: this.controller.canvas,
        }
      );

      return textField;
    }
  }

  // Update textFields with new dimensions based on table dimensions
  updateTextFieldDimensions() {
    const { _textFields } = this;
    for (const key of Object.keys(_textFields)) {
      const [row, column] = this._parseCellKey(key);
      // Make subcells in a merged cell unclickable, except the first one
      if (this._inMergedCell([row, column])) {
        const mergedCell = this._getMergedCell([row, column]);
        if (!this._cellEquals(mergedCell[0], [row, column])) {
          this._textFields[key].updateDimensions({
            left: 0,
            top: 0,
            width: 0,
            height: 0,
          });
          continue;
        }
      }

      const dimensions = this._getCellDimensions(row, column);
      if (dimensions != null) {
        const { left, top, width, height } = dimensions;
        this._textFields[key].updateDimensions({
          left: left - this.left,
          top: top - this.top,
          width,
          height,
        });
      }
    }
  }

  // textField dimensions have updated, so keep table in sync. Used e.g. when
  // text causes textField height to change
  textFieldUpdateDimensions(textField, keyOrPath: KeyPath, value: any) {
    if (keyOrPath.includes('height')) {
      const { key } = textField;
      const [row, column] = this._parseCellKey(key);
      const vectorDimensionKey = this.getVectorDimensionKey(row, true);
      let afterIndex = row;

      if (this[vectorDimensionKey] == null) return;

      const keys = [vectorDimensionKey];
      const inMergedCell = this._inMergedCell([row, column]);
      if (inMergedCell) {
        const mergedCell = this._getMergedCell([row, column]);
        // get the height for all rows in the merged cell aside from the first
        const [, endCell] = mergedCell;
        const [endRow] = endCell;
        afterIndex = endRow;
        for (let i = row + 1; i <= endRow; i++) {
          keys.push(this.getVectorDimensionKey(i, true));
        }
      }
      // Current height of all cells in merged cell
      const currentAccHeight = keys.reduce((acc, curr) => acc + this[curr], 0);
      const diff = value - currentAccHeight;
      const split = diff / keys.length;
      const rowHeights = Object.fromEntries(
        keys.map(key => [key, this[key] + split])
      );

      // Shift objects in rows interesecting merged cell
      if (inMergedCell) {
        for (let i = row + 1; i <= afterIndex; i++) {
          const cumulativeShift = split * (i - row);
          this.shiftObjectsInVector(i, cumulativeShift, true, {
            excludeObjectsInMergedCell: true,
          });
        }
      }

      // Shift objects in rows after this row
      this.shiftObjectsAfterVectorIndex(afterIndex, diff, true);
      this.set({ ...rowHeights, height: this.height + diff });
    }
  }

  _updateTextFieldToCurrentCell() {
    const activeTextField = this.activeTextField as TextField;
    const [r, c] = this._parseCellKey(activeTextField?.key);
    const [row, column] = this.currentCell!;
    if (r === row && c === column) {
      return;
    }

    const textFieldKey = this._getCellKey(row, column);
    this.activeTextField = this._textFields[textFieldKey];
  }

  // Syncs _textFields with number and size of cells. Assumes content is already
  // shifted to appropriate cells
  _updateTextFields() {
    if (!this.hasTextFields) {
      return;
    }

    // Use TextField object to create text for each cell in the table that has
    // text content
    for (let i = 0; i < this.numRows; i++) {
      for (let j = 0; j < this.numColumns; j++) {
        const textFieldKey = this._getCellKey(i, j);
        if (!this._textFields[textFieldKey]) {
          const textField = this.createTextField(i, j);
          if (textField) {
            this.addTextField(textField);
          }
        }
      }
    }

    // Sync dimensions
    this.updateTextFieldDimensions();
  }

  // Remove text fields when vector count decreases. Assumes text field content
  // has already been shifted as necessary
  _removeTextFields(keyOrPath: KeyPath, value: any) {
    // Make sure vector count actually decreased
    if (
      (typeof keyOrPath === 'string' && this[keyOrPath] < value) ||
      !this.hasTextFields
    ) {
      return;
    }

    const isRow = keyOrPath === 'numRows';
    const numRows = isRow ? value : this.numRows;
    const numColumns = isRow ? this.numColumns : value;
    const cellCount = numRows * numColumns;
    if (cellCount < Object.keys(this._textFields).length) {
      // Remove fields in last row/column
      const vectorRemovedIndex = isRow ? numColumns : numRows;
      for (let i = 0; i < vectorRemovedIndex; i++) {
        const textFieldKey = `cell_${isRow ? numRows : i}_${
          isRow ? i : numColumns
        }`;
        const textField = this._textFields[textFieldKey];
        this.removeTextField(textField);
      }
    }
  }

  get selectedTextFields(): TextField[] {
    const fields = Array<TextField>();
    if (this.hasMultipleSelectedCells()) {
      const [[r1, c1], [r2, c2]] = this._getCellRange(
        this.currentCell,
        this.previousCell
      );

      for (let i = r1; i <= r2; i++) {
        for (let j = c1; j <= c2; j++) {
          const textFieldKey = this._getCellKey(i, j);
          const textField = this._textFields[textFieldKey];
          if (textField) fields.push(textField);
        }
      }
    } else if (this.hasSelectedCell()) {
      let [row, column] = this.currentCell;
      if (this._inMergedCell([row, column])) {
        [row, column] = this._getMergedCell([row, column])[0];
      }
      const textFieldKey = this._getCellKey(row, column);
      const textField = this._textFields[textFieldKey];
      if (textField) fields.push(textField);
    } else {
      fields.push(
        ...Object.values(this._textFields).filter(
          field => field && field.textContent?.length > 0
        )
      );
    }

    if (fields.length > 0) {
      return fields;
    }

    return Object.values(this._textFields).filter(Boolean);
  }

  _resizeDimension(
    newDimension: number,
    isRow = true,
    corner: 'tl' | 'tr' | 'br' | 'bl' | 'mt' | 'mr' | 'mb' | 'ml',
    objects: fabric.Object[][]
  ) {
    const vectorCount = isRow ? this.numRows : this.numColumns;
    const dimension = isRow ? this.height : this.width;
    const diff = dimension - newDimension;
    const proportionalDiff = diff / vectorCount;
    let adjustedDimension = dimension;

    const fromTopLeft =
      corner === 'mt' ||
      corner === 'ml' ||
      corner === 'tl' ||
      (corner === 'tr' && isRow) ||
      (corner === 'bl' && !isRow);

    // Gather space remaining in each vector for shrinking
    const spacesInVectors: number[] = [];
    for (let i = 0; i < vectorCount; i++) {
      spacesInVectors.push(this._vectorAvailableSpace(i, isRow, objects[i]));
    }

    // Determine amount each vector will grow/shrink by
    const shiftAmounts: number[] = [];
    for (let i = 0; i < vectorCount; i++) {
      shiftAmounts.push(Math.min(spacesInVectors[i], proportionalDiff));
    }

    // Accumulate amount each previous vector has adjusted by
    let cumulativeShift = 0;

    if (fromTopLeft) {
      for (let i = vectorCount - 1; i >= 0; i--) {
        const vectorAdjustment = shiftAmounts[i];
        cumulativeShift += vectorAdjustment;
        const shift = cumulativeShift;
        const vectorKey = this.getVectorDimensionKey(i, isRow);
        this.shiftObjects(objects[i], shift, isRow);
        this.set(vectorKey, this[vectorKey] - vectorAdjustment);
        adjustedDimension -= vectorAdjustment;
      }
    } else {
      for (let i = 0; i < vectorCount; i++) {
        const vectorAdjustment = shiftAmounts[i];
        const shift = -cumulativeShift;
        cumulativeShift += vectorAdjustment;
        const vectorKey = this.getVectorDimensionKey(i, isRow);
        this.shiftObjects(objects[i], shift, isRow);
        this.set(vectorKey, this[vectorKey] - vectorAdjustment);
        adjustedDimension -= vectorAdjustment;
      }
    }
    return adjustedDimension;
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    if (
      typeof keyOrPath === 'string' &&
      ['numRows', 'numColumns'].includes(keyOrPath)
    ) {
      this._updateVectorSizeControls(
        keyOrPath as 'numRows' | 'numColumns',
        value
      );
      this._removeTextFields(keyOrPath, value);
      if (this.controller.selection === this) {
        this.controller.actionForceRerenderObjectToolbar();
      }
    }

    if (keyOrPath.includes('merged_cell')) {
      this._syncMergedCells(keyOrPath, value);
    }

    // Do not set scale on tables
    if (
      typeof keyOrPath === 'string' &&
      ['scaleX', 'scaleY'].includes(keyOrPath)
    ) {
      return this;
    }

    if (keyOrPath === 'hasHeaderRow' || keyOrPath === 'hasHeaderColumn') {
      this._updateHeaderStyles(keyOrPath);
    }

    super._set(keyOrPath, value);

    // Update text fields when vector dimension is updated
    if (this._isVectorDimensionKey(keyOrPath)) {
      this._updateTextFields();
    }

    if (keyOrPath === 'canvas' && value) {
      this.initGroupSelectorListeners();
    }

    return this;
  }

  textClass(): string | null {
    return 'frameText';
  }

  showTitle() {
    return true;
  }

  calcTextMargin() {
    return { top: 0, left: 0, right: 0, bottom: SPACER_2 };
  }

  _setCellProperty(key: string, value: any) {
    // Apply text properties to selected text fields directly
    if (this.cellTextProperties.includes(key)) {
      this.selectedTextFields.forEach(field => {
        const textField = this._textFields[field.key];
        textField.set(key, value);
      });
    }

    // We are acting on a selected cell
    if (this.hasSelectedCell()) {
      const startCell = this.currentCell;
      const endCell =
        this.shouldMultiSelectCell && this.previousCell
          ? this.previousCell
          : startCell;
      // Get start and end of cell range
      let [c1, c2] = this._getCellRange(startCell, endCell);

      // Adjust the range for selected merged cells
      if (this._inMergedCell(c1)) {
        c1 = this._getMergedCell(c1)[0];
      }
      if (this._inMergedCell(c2)) {
        c2 = this._getMergedCell(c2)[1];
      }

      this._applyCellPropertiesForRange(c1, c2, key, value);
    } else if (this.cellProperties.includes(key)) {
      // For these values, we need to always update to ensure cells match table
      // properties
      this._setAllCellsProp(key, value);
      this._set(key, value);
    } else if (this.cellTextProperties.includes(key)) {
      // TODO clean this up and try to dry up above section as well
      for (let i = 0; i < this.numRows; i++) {
        for (let j = 0; j < this.numColumns; j++) {
          const textFieldKey = this._getCellKey(i, j);
          const textField = this._textFields[textFieldKey];
          if (textField.textContent?.length > 0) {
            textField.set(key, value);
          }
        }
      }
    } else {
      super.set(key, value);
    }
  }

  _setObjectOnObjects(fieldValues: Record<string, unknown>) {
    for (const key in fieldValues) {
      this._setCellProperty(key, fieldValues[key]);
      // Locally save text related props to propagate to new text fields. These
      // fields are not serilized on Table, so they won't persist or affect other users
      if (!this.hasSelectedCell() && this.cellTextProperties.includes(key)) {
        this[key] = fieldValues[key];
      }
    }
  }

  // Override the applyPaste method from StyleCopyPasteBehaviors
  applyPaste(styles: Record<string, unknown>): void {
    const controlled = this.getControlledProperties() ?? {};
    const allowed = Object.fromEntries(
      Object.entries((this as any).filterToAllowedFields(styles)).filter(
        ([k]) => !(k in controlled)
      )
    );

    // If we have a selected cell, apply the styles to the selected cells
    if (this.hasSelectedCell()) {
      // Apply cell properties (fill, opacity, etc.)
      const cellProps = {};
      this.cellProperties.forEach(prop => {
        if (prop in allowed) {
          cellProps[prop] = allowed[prop];
        }
      });

      if (Object.keys(cellProps).length > 0) {
        // Apply cell properties to selected cell(s)
        Object.entries(cellProps).forEach(([key, value]) => {
          this._setCellProperty(key, value);
        });
      }

      // Apply text properties to selected cells' text fields
      const textProps = {};
      this.cellTextProperties.forEach(prop => {
        if (prop in allowed) {
          textProps[prop] = allowed[prop];
        }
      });

      if (Object.keys(textProps).length > 0) {
        this.selectedTextFields.forEach(field => {
          if (field) {
            Object.entries(textProps).forEach(([key, value]) => {
              field.set(key, value);
            });
          }
        });
      }
    } else {
      // Apply to table as a whole
      this._setObjectOnObjects(allowed);
    }
  }

  setHovered(): boolean {
    // this should always be set, but sometimes isn't according to sentry
    const object = this.canvas?.getActiveObject() as fabric.Object | undefined;

    if (!object) {
      return false;
    }

    // Avoid setting hovered cells when the table is part of the selection
    if (
      isSimpleSelection(object) &&
      object._objects.find(obj => obj.id === this.id)
    ) {
      this.clearHovered();
      return false;
    }

    this.hoveredPoints = isSimpleSelection(object)
      ? (this.hoveredPoints = object._objects
          .filter(obj => this.containsObject(obj))
          .map(obj => obj.getCenterPoint()))
      : (this.hoveredPoints = this.containsObject(object)
          ? [object.getCenterPoint()]
          : []);

    this.hovered = true;
    return true;
  }

  clearHovered() {
    this.hoveredPoints = null;
    this.hovered = false;
  }

  // Ensure that the table is selected at least once before allowing a cell to
  // be selected
  _setSelected() {
    this.selected = true;
    this.canvas.requestRenderAll();
  }

  _clearSelected() {
    this.selected = false;
  }

  async _handleKeyDown(e: KeyboardEvent) {
    // Edit table title when the user presses Enter
    if (!this.hasSelectedCell() && e.key === 'Enter') {
      e.preventDefault();
      this.activeTextField = this;
      this.activeTextField.startTextEditing();
    } else if (
      !this.shouldMultiSelectCell &&
      this.hasSelectedCell() &&
      this.activeTextField
    ) {
      if (
        e.target instanceof HTMLElement &&
        e.target.className.includes('colorPickerHexInput')
      ) {
        return;
      }

      if (this.activeTextField.textEditing) {
        if (e.key === 'Escape') {
          this.activeTextField.endTextEditing();
          this.controller.actionFocus(false);
          this.controller.actionEnableSelectionKeys();
          return false;
        }
      }

      // Ensure that key presses go to text editor while editing
      if (this.activeTextField.textEditing) {
        return;
      }

      const { key } = e;
      if (key === 'Backspace' || key === 'Delete') {
        this.activeTextField.set('textContent', null);
        this.canvas.requestRenderAll();
      } else if (key === 'Tab') {
        // Select next table cell. If editing, should go to next cell while still editing
        this._selectNextCell(true, true, !e.shiftKey);
        // Stop tab from scrolling through browser. Is this an accessibility issue?
        e.preventDefault();
        this._updateTextFieldToCurrentCell();
      } else if (key.includes('Arrow') && !this.activeTextField.textEditing) {
        const forwards = key.includes('Right') || key.includes('Down');
        const isRow = key.includes('Left') || key.includes('Right');
        this._selectNextCell(isRow, false, forwards);
        this._updateTextFieldToCurrentCell();
        this.controller.actionRerenderToolbar();
      } else if (
        key === 'Shift' ||
        key === 'Control' ||
        key === 'Alt' ||
        key === 'Meta'
      ) {
        // Do nothing when modifier is pressed on its own
        return false;
      } else {
        let [r, c] = this.currentCell;
        if (this._inMergedCell(this.currentCell)) {
          [r, c] = this._getMergedCell(this.currentCell)[0];
        }
        const textFieldKey = this._getCellKey(r, c);
        const textField = this._textFields[textFieldKey];
        if (!textField.textEditing) {
          e.preventDefault();
          this.activeTextField = textField;
          const modifierPressed =
            (_isMac && e.metaKey) || (!_isMac && e.ctrlKey);
          if (key === 'Enter') {
            await textField.startTextEditing(true);
            textField.applyDefaultProperties({ force: true });
          } else if (['c', 'x'].includes(key) && modifierPressed) {
            this._handleTableCellCopy(textField, key === 'x');
          } else if (key === 'v' && modifierPressed) {
            await this._handleTableCellPaste(textField);
          } else if (/^[a-zA-Z0-9]$/.test(key)) {
            // Limit edit start to alphanumeric to avoid function keys and similar
            textField.textContent = key;
            await textField.startTextEditing();

            // Re-apply default styles on all to style initial input
            textField.applyDefaultProperties({
              force: true,
              operateOnAll: true,
            });
          }
        }
      }
    }
  }

  // Handle copy/cut when table cell selected
  _handleTableCellCopy(textField: TextField, isCut = false) {
    if (textField.textContent) {
      const clipboardItem = new ClipboardItem({
        'text/plain': new Blob([sanitizeHtml(textField.textContent)], {
          type: 'text/plain',
        }),
        'text/html': new Blob([textField.textContent], {
          type: 'text/html',
        }),
      });

      void navigator.clipboard.write([clipboardItem]);
      if (isCut) {
        textField.set('textContent', '');
        this.canvas.requestRenderAll();
      }
    }
  }

  /**
   * Converts the table to HTML format for clipboard operations
   * @returns HTML string representation of the table
   */
  toClipboardHTML(): string {
    let html = '<table style="border-collapse: collapse; width: 100%;">';

    if (this.hasHeaderRow) {
      html += '<thead>';
    }

    for (let i = 0; i < this.numRows; i++) {
      html += '<tr>';

      // Process cells in this row
      for (let j = 0; j < this.numColumns; j++) {
        // Get cell dimensions
        const rowHeight = this[this.getVectorDimensionKey(i, true)];
        const colWidth = this[this.getVectorDimensionKey(j, false)];

        // Get text field for this cell
        const textFieldKey = this._getCellKey(i, j);
        const textField = this._textFields[textFieldKey];

        // Get text alignment properties
        const textAlign = textField?.textAlign || 'left';
        const verticalAlign = textField?.verticalAlign || 'middle';

        // Skip cells that are part of a merged cell but not the starting cell
        if (this._inMergedCell([i, j])) {
          const mergedCell = this._getMergedCell([i, j]);
          if (!this._cellEquals(mergedCell[0], [i, j])) {
            continue;
          }

          // For merged cells, add colspan and rowspan attributes
          const [start, end] = mergedCell;
          const rowSpan = end[0] - start[0] + 1;
          const colSpan = end[1] - start[1] + 1;

          // Calculate total width and height for merged cells
          let totalWidth = 0;
          let totalHeight = 0;

          for (let col = start[1]; col <= end[1]; col++) {
            totalWidth += this[this.getVectorDimensionKey(col, false)];
          }

          for (let row = start[0]; row <= end[0]; row++) {
            totalHeight += this[this.getVectorDimensionKey(row, true)];
          }

          // Get cell styling
          const { fill, opacity } = this.getCellFillAndOpacity(i, j);

          // Map fabric's verticalAlign values to CSS values
          let cssVerticalAlign = String(verticalAlign);
          if (verticalAlign === 'flex-start') cssVerticalAlign = 'top';
          else if (verticalAlign === 'flex-end') cssVerticalAlign = 'bottom';

          const cellStyle = `background-color: ${fill}; opacity: ${opacity}; width: ${totalWidth}px; height: ${totalHeight}px; text-align: ${textAlign}; vertical-align: ${cssVerticalAlign};`;

          // Determine if this is a header cell
          const isHeader =
            (this.hasHeaderRow && i === 0) || (this.hasHeaderColumn && j === 0);
          const cellTag = isHeader ? 'th' : 'td';

          // Get cell content
          const content = textField?.textContent
            ? textField.textContent
            : '&nbsp;';

          // Add the cell with appropriate attributes
          html += `<${cellTag} style="${cellStyle}" ${
            rowSpan > 1 ? `rowspan="${rowSpan}"` : ''
          } ${
            colSpan > 1 ? `colspan="${colSpan}"` : ''
          }>${content}</${cellTag}>`;
        } else if (!this._inMergedCell([i, j])) {
          // Regular cell processing
          const { fill, opacity } = this.getCellFillAndOpacity(i, j);

          // Map fabric's verticalAlign values to CSS values
          let cssVerticalAlign = String(verticalAlign);
          if (verticalAlign === 'flex-start') cssVerticalAlign = 'top';
          else if (verticalAlign === 'flex-end') cssVerticalAlign = 'bottom';

          const cellStyle = `background-color: ${fill}; opacity: ${opacity}; width: ${colWidth}px; height: ${rowHeight}px; text-align: ${textAlign}; vertical-align: ${cssVerticalAlign};`;

          // Determine if this is a header cell
          const isHeader =
            (this.hasHeaderRow && i === 0) || (this.hasHeaderColumn && j === 0);
          const cellTag = isHeader ? 'th' : 'td';

          // Get cell content
          const content = textField?.textContent
            ? textField.textContent
            : '&nbsp;';

          html += `<${cellTag} style="${cellStyle}">${content}</${cellTag}>`;
        }
      }

      html += '</tr>';

      // Close thead if this was the header row
      if (this.hasHeaderRow && i === 0) {
        html += '</thead><tbody>';
      }
    }

    // Close tbody if we had a header row
    if (this.hasHeaderRow) {
      html += '</tbody>';
    }

    html += '</table>';
    return html;
  }

  // Handle paste when table cell selected
  async _handleTableCellPaste(textField: TextField) {
    try {
      const clipboardItems = await navigator.clipboard.read();
      let plainTextContent: string | null = null;
      let htmlContent: string | null = null;
      let hasImage = false;
      // The clipboard may contain multiple data types.
      // We want to prioritize content in this order:
      // 1. Image
      // 2. HTML
      // 3. Plain text
      for (const item of clipboardItems) {
        for (const type of item.types) {
          if (type.startsWith('image/')) {
            // If there is image data on the clipboard, break and
            // handle it with ToolPaste.action()
            hasImage = true;
            break;
          }
          if (type === 'text/html') {
            const blob = await item.getType(type);
            htmlContent = await blob.text();
          }
          if (type === 'text/plain') {
            const text = await navigator.clipboard.readText();
            // If the content starts with { then assume it's JSON and
            // that the user has copied whiteboard objects.
            // Ignore it here and let ToolPaste.action() handle it.
            if (text[0] !== '{') {
              plainTextContent = text;
            }
          }
        }
      }

      // If we have an image, skip this block and use ToolPaste.action()
      if (!hasImage) {
        const textToPaste = htmlContent || plainTextContent;
        if (textToPaste) {
          // If we have plain text or HTML that does not appear to be JSON,
          // paste directly into the table cell
          textField.textContent = textToPaste;
          await textField.startTextEditing();
          textField.applyDefaultProperties({
            force: true,
            operateOnAll: true,
          });
          // Return here so ToolPaste.action() does not get called.
          return;
        }
      }
      // For pasting JSON content or images, use logic in ToolPaste.action()
      await tools.ToolPaste.action({ controller: this.controller });
    } catch (e) {
      console.log(e);
      this.controller.actionShowPasteUnsupportedMessage();
    }
  }

  // Highlighted cell must have a row and cell coordinate
  hasSelectedCell(): this is { currentCell: Cell } {
    return Boolean(this.currentCell);
  }

  hasMultipleSelectedCells(): this is {
    shouldMultiSelectCell: true;
    currentCell: Cell;
    previousCell: Cell;
  } {
    return (
      this.shouldMultiSelectCell &&
      this.previousCell?.length === 2 &&
      this.currentCell?.length === 2
    );
  }

  // Check if current and previous cells are mergable. Cells are mergable if they
  // form a rectangle. If merging with a merged cell, the entire cell must be
  // taken into account
  get mergeable(): boolean {
    // Must be in a valid multi select
    if (!this.hasMultipleSelectedCells()) {
      return false;
    }

    const { currentCell, previousCell } = this;

    // Cannot merge the same cell with itself
    if (this._cellEquals(currentCell, previousCell)) {
      return false;
    }

    let [start, end] = this._getCellRange(currentCell, previousCell);
    const cell1Merged = this._inMergedCell(currentCell);
    const cell2Merged = this._inMergedCell(previousCell);

    if (cell1Merged && cell2Merged) {
      const mergedCell1 = this._getMergedCell(currentCell);
      const mergedCell2 = this._getMergedCell(previousCell);
      // Cells aren't mergeable if current and previous are in the same cell
      if (this._mergedCellEquals(mergedCell1, mergedCell2)) {
        return false;
      }

      [start, end] = this._cellLessThan(currentCell, previousCell)
        ? [mergedCell1[0], mergedCell2[1]]
        : [mergedCell2[0], mergedCell1[1]];
    }

    if (start === null || end === null) {
      return false;
    }

    return this.mergedCells.every(cell => {
      const [[r1, c1], [r2, c2]] = cell;
      const inRange =
        ((r1 >= start[0] && r1 <= end[0]) ||
          (r2 >= start[0] && r2 <= end[0]) ||
          (start[0] >= r1 && end[0] <= r2)) &&
        ((c1 >= start[1] && c1 <= end[1]) ||
          (c2 >= start[1] && c2 <= end[1]) ||
          (start[1] >= c1 && end[1] <= c2));
      const fullyInRange =
        r1 >= start[0] &&
        r1 <= end[0] &&
        r2 >= start[0] &&
        r2 <= end[0] &&
        c1 >= start[1] &&
        c1 <= end[1] &&
        c2 >= start[1] &&
        c2 <= end[1];
      const inline =
        (r1 === start[0] && r2 === end[0]) ||
        (c1 === start[1] && c2 === end[1]);

      if (inRange && !(fullyInRange || inline)) {
        return false;
      }
      return true;
    });
  }

  // Expose whether unmerging is allowed currently
  get unmergeable(): boolean {
    const { currentCell, previousCell } = this;

    if (!currentCell) {
      return false;
    }

    if (!this._inMergedCell(currentCell)) {
      return false;
    }

    if (this.shouldMultiSelectCell) {
      return (
        !!previousCell &&
        this._inMergedCell(previousCell) &&
        this._mergedCellEquals(
          this._getMergedCell(currentCell),
          this._getMergedCell(previousCell)
        )
      );
    }

    return true;
  }

  _cellLessThan(cell1: Cell, cell2: Cell): boolean {
    if (cell1[0] === cell2[0]) {
      return cell1[1] < cell2[1];
    }
    return cell1[0] < cell2[0];
  }

  _cellEquals(cell1: Cell, cell2: Cell): boolean {
    return cell1[0] === cell2[0] && cell1[1] === cell2[1];
  }

  _isSingleCell(cell: MergedCell): boolean {
    const [start, end] = cell;
    return this._cellEquals(start, end);
  }

  _mergedCellEquals(cell1: MergedCell, cell2: MergedCell): boolean {
    const [start1, end1] = cell1;
    const [start2, end2] = cell2;
    return this._cellEquals(start1, start2) && this._cellEquals(end1, end2);
  }

  _copyMergedCell(cell: MergedCell): MergedCell {
    const [start, end] = cell;
    return [
      [start[0], start[1]],
      [end[0], end[1]],
    ];
  }

  showLockTool() {
    return this.locked || !this.currentCell;
  }

  fontSizeWithDefault() {
    if (
      this.hasSelectedCell() &&
      this.selectedTextFields.length > 0 &&
      this.selectedTextFields.every(
        textField =>
          textField?.fontSize &&
          textField.fontSize === this.selectedTextFields[0]?.fontSize
      )
    ) {
      return this.selectedTextFields[0].fontSize;
    }
    return super.fontSizeWithDefault();
  }

  /** Sets dimensions for the table's columns and rows based on the provided arrays of widths and heights */
  setTableDimensions(colWidths: number[], rowHeights: number[]) {
    const properties = {
      numColumns: colWidths.length,
      numRows: rowHeights.length,
    };
    for (let i = 0; i < colWidths.length; i++) {
      properties[this.getVectorDimensionKey(i, false)] = colWidths[i];
    }
    for (let i = 0; i < rowHeights.length; i++) {
      properties[this.getVectorDimensionKey(i, true)] = rowHeights[i];
    }
    this.set(properties);
    this.normalizeDimensions();
  }

  isHoveringDragControl(): boolean {
    return !!Object.entries(this.controls).find(
      ([key, o]) => key.includes('Drag') && o.hovered
    );
  }

  private _updateHeaderStyles(key: 'hasHeaderRow' | 'hasHeaderColumn') {
    const isRow = key === 'hasHeaderRow';
    const cells = this._getVectorCellsCoords(0, isRow);

    for (const [row, col] of cells) {
      const textFieldKey = this._getCellKey(row, col);
      const textField = this._textFields[textFieldKey];
      if (textField) {
        void textField.renderTextToImage();
      }
    }
  }

  textFieldShouldHaveHeaderStyle(textField: TextField) {
    const [, rowIndex, colIndex] = textField.key.split('_').map(Number);
    if (this.hasHeaderRow && rowIndex === 0) {
      return true;
    }
    if (this.hasHeaderColumn && colIndex === 0) {
      return true;
    }
    return false;
  }

  private initTableMouseListeners() {
    // Set table specific mousedown listener
    this.on('mousedown:before', event => {
      // Overrides frame default behavior. When zoomed in on a table, the table should
      // not be movable, so we clear the target in mousedown:before. However, in order
      // to support cell text editing, we only do this if no cell is selected. This
      // allows handleZoomedDoubleClick to set the activeTextField when zoomed in
      if (
        event.pointer &&
        !this.containsPointInText(event.pointer) &&
        !this.edgesVisible &&
        this.canvas._target === this
      ) {
        if (!this.hasSelectedCell() && !this.selected) {
          // Only prevent selection if no cells are selected and the table is already selected
          this.canvas._target = null;
        }
      }
    });

    this.on('mousedblclick', event => {
      /*
       * Special case handling for zoomed in tables. Tables have slightly different
       * behavior from frames when zoomed in, specifically around editing text cells
       * - Frames and tables clear the selected object in mousedown:before, in order
       *   to avoid moving the table when very zoomed in
       * - EmbeddedTextBehaviors mousedblclick depends on a selected object, and will
       *   clear the activeTextField if no object is selected
       * - In order to make table cells editable when zoomed in, we need to manually
       *   set the activeTextField in mousedblclick to override EmebeddedTextBehaviors default
       * - We also start text editing on the title if the user double clicks there
       *
       */

      if (this.locked) {
        return;
      }

      if (event.pointer && this.containsPointInText(event.pointer)) {
        this.canvas.setActiveObject(this);
        this.activeTextField = this;
        this.maybeStartTextEditing(false);
      } else if (this.edgesVisible && !this.hasSelectedCell()) {
        // If no cell is selected, handle normally for table title editing
        this.activeTextField = this;
        void this.activeTextField?.startTextEditing(true);
      } else if (!this.edgesVisible && !this.hasMultipleSelectedCells()) {
        // Otherwise, continue with cell-specific behavior
        this.setSelectedTextField(event);
      }
    });

    this.on('mousedown', () => {
      // If the target of a click is a corner, clear selected field to avoid
      // highlighting cell on mouse up
      const corner = this.__corner;
      if (corner) {
        this._clearSelected();
        this.clearSelectedCells();
      }
    });

    this.on('mouseup:before', event => {
      const containsPointInText =
        event.pointer && this.containsPointInText(event.pointer);
      // If selector contains an object other than the table, select that object
      if (this.canvas.hasGroupSelector) {
        if (this.canvas.groupSelectorIntersectsOther(this)) {
          this.canvas._target = null;
        }
        this._shouldSelectTable = false;
      } else if (!containsPointInText) {
        this._shouldSelectTable = true;
      }
    });

    this.on('mouseup', eventData => {
      // If right-clicking within a group of selected cells, allow context menu
      // to populate within selected cells. Otherwise, a right click will select
      // a new cell and the context menu will open for that cell specifically.
      if (isRightClick(eventData.e, this.canvas.lastMouseDown)) {
        const point = this.controller.contextMenuClickPoint;
        if (point) {
          const canvasPoint = this.canvas.restorePointerVpt(point);
          const cellDimensions =
            this._getCellDimensionsFromPointer(canvasPoint);
          if (cellDimensions && this.hasMultipleSelectedCells()) {
            const cell: Cell = [cellDimensions.row, cellDimensions.column];
            const cell1 = this.currentCell;
            const cell2 = this.previousCell;
            if (
              cell1 &&
              cell2 &&
              // check if in merged cell for cell1 or cell2
              (this.cellContainsCell(cell1, cell) ||
                this.cellContainsCell(cell2, cell) ||
                // otherwise, check if in the range of the selected cells
                this.cellInRange(cell, cell1, cell2))
            ) {
              return;
            }
          }
        }
      }

      // If there was a groupSelector just prior to a mouseup event, the
      // groupSelector events will handle selection as necessary. We can't rely
      // on eventData.isClick because it registers very tiny (1px) drags, which
      // makes for a poor experience when trying to select cells.
      if (this.shouldSelectTable() && this.selected) {
        this._selectCell(eventData);
      }
      this._setSelected();
    });

    // Make table the activeTextField when multiselecting so that text tools
    // apply to entire selection
    this.on('mouseup', () => {
      if (this.shouldMultiSelectCell) {
        this.activeTextField = this;
      }
    });

    this.on('mousemove', eventData => {
      this._handleMergedCellControls(eventData);
    });

    this.on('mousemove', () => {
      // When cell is selected, the table is not movable, so show the appropriate
      // cursor.
      if (
        this.hasSelectedCell() ||
        (this.hasMultipleSelectedCells() && !this.__corner)
      ) {
        this.hoverCursor = 'default';
      } else {
        this.hoverCursor = 'move';
      }
    });

    // Disable drag selection when hovering over row/col drag controls so can still be dragged
    this.on('mousemove', () => {
      if (this.isHoveringDragControl()) {
        this.allowsDragSelection = false;
      } else {
        this.allowsDragSelection = this.hasSelectedCell();
      }
    });
  }

  preventTextEditing() {
    return false;
  }

  hasEmbeddedText() {
    return true;
  }

  private shouldSelectTable() {
    return !this.locked && this._shouldSelectTable;
  }
}

export default Table;
